import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db, getCollectionName } from './firebase';
import {
  collection, query, where, onSnapshot, getDocs, getDoc, doc,
  addDoc, setDoc, updateDoc, deleteDoc, serverTimestamp, arrayUnion,
} from './firestore-compat';
import { uploadArquivo } from './arquivos';
import { supabase } from './supabase';
import { signOut as sairDoSistema } from './auth-compat';
import InepPerfil from './inep/InepPerfil';
import InepRodape from './inep/InepRodape';
import InepPersonalizacaoAdmin from './inep/InepPersonalizacaoAdmin';
import { VideoProprio, ehVideoProprio, enviarVideo, duracaoDoArquivo, LIMITE_VIDEO_MB } from './inep/videoInep';
import { RODAPE_PADRAO } from './inep/InepRodape';
import CertificadoPrevia from './inep/CertificadoPrevia';
import {
  montarHtml, abrirJanelaCertificado, imprimirNaJanela, urlValidacao,
  dataBR, dataExtenso, formatarCpf, cpfValido, nomeProprio, notaBR,
} from './inep/certificadoOficial';
import {
  TEXTOS_PADRAO, VALORES_PADRAO, TIMELINE_PADRAO, UNIDADES_PADRAO, estiloDoTema,
} from './inep/inepPersonalizacao';
import './InepEnsino.css';

// Conteúdo institucional ("Quem somos"), textos e cores: padrões em inep/inepPersonalizacao.js,
// valores salvos em inep_config/geral (Personalização).
const CORES_CURSO = {
  blue:  { css: 'inep-hero-blue',  hex: 'var(--blue)' },
  coral: { css: 'inep-hero-coral', hex: 'var(--coral)' },
  mint:  { css: 'inep-hero-mint',  hex: 'var(--mint)' },
};

// ── Gamificação: emblemas (calculados a partir dos dados que já existem — sem nova tabela) ──
const BADGES = [
  { id: 'primeiro_passo', ic: '👣', nome: 'Primeiro passo', desc: 'Concluiu a primeira aula', check: s => s.aulasFeitas >= 1 },
  { id: 'primeiro_curso',  ic: '🎓', nome: 'Primeiro curso', desc: 'Concluiu o primeiro curso', check: s => s.cursosConcluidos >= 1 },
  { id: 'tres_cursos',     ic: '📚', nome: 'Trilha completa', desc: 'Concluiu 3 cursos', check: s => s.cursosConcluidos >= 3 },
  { id: 'estudioso',       ic: '🔥', nome: 'Estudioso', desc: 'Concluiu 10 aulas', check: s => s.aulasFeitas >= 10 },
  { id: 'nota_maxima',     ic: '💯', nome: 'Nota máxima', desc: 'Tirou 100% em uma avaliação', check: s => s.notaMax >= 100 },
  { id: 'aprovado',        ic: '✅', nome: 'Sempre aprovado', desc: 'Média de avaliações acima de 90%', check: s => s.notas.length > 0 && s.mediaNotas >= 90 },
  { id: 'colecionador',    ic: '🏅', nome: 'Colecionador', desc: 'Recebeu 5 certificados', check: s => s.cursosConcluidos >= 5 },
  { id: 'sociavel',        ic: '💬', nome: 'Sociável', desc: 'Participou do chat com instrutores', check: s => s.mensagensEnviadas >= 1 },
];

const ADMIN_TABS = [
  { id: 'painel',       label: 'Painel geral',        ic: '📊', group: 'VISÃO GERAL' },
  { id: 'alunos',       label: 'Alunos',              ic: '👥', group: 'GESTÃO' },
  { id: 'cursos_admin', label: 'Cursos & módulos',    ic: '🎒', group: 'GESTÃO' },
  { id: 'turmas',       label: 'Turmas',              ic: '🧑‍🤝‍🧑', group: 'GESTÃO' },
  { id: 'acompanhamento', label: 'Acompanhamento',    ic: '🧭', group: 'GESTÃO' },
  { id: 'avaliacoes_notas', label: 'Notas', ic: '📝', group: 'GESTÃO' },
  { id: 'certificados_admin', label: 'Certificados',  ic: '🏅', group: 'GESTÃO' },
  { id: 'mensagens_admin', label: 'Mensagens',        ic: '💬', group: 'GESTÃO' },
  { id: 'relatorios',   label: 'Relatórios',          ic: '📋', group: 'PLATAFORMA' },
  { id: 'personalizacao', label: 'Personalização',    ic: '🎨', group: 'PLATAFORMA' },
  { id: 'acesso',       label: 'Controle de acesso',  ic: '🔐', group: 'PLATAFORMA' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function slugEmail(email) { return (email || '').toLowerCase().replace(/[^a-z0-9]/gi, '_'); }
function matriculaId(cursoId, email) { return `${cursoId}__${slugEmail(email)}`; }
// Nota mínima de aprovação (0–100): "Média mínima" do curso (0–10) × 10; sem ela, 70
function minimoAprovacao(curso) {
  const m = Number(String(curso?.notaMinima ?? '').replace(',', '.'));
  return Number.isFinite(m) && m > 0 ? m * 10 : 70;
}
function tsVal(v) {
  if (!v) return 0;
  const d = v?.toDate ? v.toDate() : new Date(v);
  return isNaN(d) ? 0 : d.getTime();
}
function fmtData(v) {
  if (!v) return '—';
  const d = v?.toDate ? v.toDate() : new Date(v);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('pt-BR');
}
function toYoutubeEmbed(url) {
  if (!url) return '';
  const m = url.match(/(?:youtu\.be\/|v=|embed\/)([a-zA-Z0-9_-]{6,})/);
  const id = m ? m[1] : (/^[a-zA-Z0-9_-]{6,}$/.test(url) ? url : null);
  return id ? `https://www.youtube.com/embed/${id}` : '';
}
function flattenAulas(curso) {
  const out = [];
  (curso?.modulos || []).forEach(mod => {
    (mod.aulas || []).forEach(a => out.push({ ...a, moduloId: mod.id, moduloTitulo: mod.titulo }));
  });
  return out;
}
// Turmas: curso com visibilidade "turmas" só aparece para quem está numa das turmas dele.
// Curso sem o campo (criados antes das turmas) continua visível para todos.
function cursoVisivelParaTurmas(curso, minhasTurmas) {
  if (curso?.visibilidade !== 'turmas') return true;
  return (curso.turmaIds || []).some(t => minhasTurmas.has(t));
}
function membroId(turmaId, email) { return `${turmaId}__${slugEmail(email)}`; }
function progressoCurso(curso, matricula) {
  const total = flattenAulas(curso).length;
  const feitas = (matricula?.aulasFeitas || []).length;
  return total ? Math.min(100, Math.round(feitas / total * 100)) : 0;
}
function downloadCsv(filename, rows) {
  const csv = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Mini-componentes ────────────────────────────────────────────────────────────
function KpiRing({ p, color, hole, label, sub }) {
  return (
    <div className="inep-kpi">
      <div className="inep-ring" style={{ '--p': p, '--ring-color': color }}><div className="inep-hole">{hole}</div></div>
      <div className="inep-ktxt"><b>{label}</b><span>{sub}</span></div>
    </div>
  );
}
function Field({ label, children }) {
  return <div className="inep-f-field" style={{ marginBottom: 12 }}><label>{label}</label>{children}</div>;
}
function Modal({ title, onClose, wide, children }) {
  return (
    <div className="inep-modal" onClick={onClose}>
      <div className={`inep-modalbox ${wide ? 'inep-wide' : ''}`} onClick={e => e.stopPropagation()}>
        <button className="inep-close-x" onClick={onClose}>✕</button>
        <h2 style={{ marginTop: 0 }}>{title}</h2>
        {children}
      </div>
    </div>
  );
}
// Certificado oficial com dados de exemplo (tela "Modelo de certificado" do admin)
const EXEMPLO_CERT = {
  aluno_nome: 'Maria Fernanda de Souza Lima', aluno_cpf: '000.000.000-00', aluno_frequencia: '100%',
  curso_titulo: 'Primeiros Socorros em Pediatria', curso_responsavel: 'Dr. André Ricardo A. da Silva', curso_nota_minima: '7,0',
  curso_modulos: ['Módulo 1 – Avaliação inicial da criança', 'Módulo 2 – Obstrução de vias aéreas', 'Módulo 3 – Reanimação cardiopulmonar pediátrica', 'Módulo 4 – Convulsões e febre'],
  certificado_dia_inicio: '01/09/2026', certificado_dia_fim: '30/09/2026', certificado_carga_horaria: '40 horas', certificado_nota: '9,2',
  certificado_data_emissao: '12 de outubro de 2026', certificado_validacao_url: 'gestaoquality.com.br/validar/EXEM-PLO1', cod: 'EXEM-PLO1',
  ead_nome: 'INEP – Instituto de Educação e Pesquisa do Prontobaby', ead_cnpj: '28.667.694/0001-34', ead_cidade: 'Rio de Janeiro',
};
function ModeloOficialExemplo() {
  const [html, setHtml] = useState('');
  const [erro, setErro] = useState('');
  const [largura, setLargura] = useState(900);
  const ref = useRef(null);
  useEffect(() => { montarHtml(EXEMPLO_CERT).then(setHtml).catch(e => setErro(e.message)); }, []);
  useEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([e]) => setLargura(e.contentRect.width));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  const PAG_L = 1123, PAG_A = 794; // A4 paisagem em px (96 dpi)
  const escala = Math.min(1, largura / PAG_L);
  return (
    <div ref={ref} className="inep-card" style={{ padding: 0, overflow: 'hidden', height: html ? PAG_A * 2 * escala + 40 : 120 }}>
      {erro && <div style={{ padding: 20, color: 'var(--coral-deep)' }}>Não foi possível carregar o modelo: {erro}</div>}
      {!html && !erro && <div style={{ padding: 20, color: 'var(--muted)' }}>Carregando o modelo oficial…</div>}
      {html && (
        <iframe title="Modelo oficial do certificado" srcDoc={html} scrolling="no"
          style={{ width: PAG_L, height: PAG_A * 2 + 40, border: 0, transform: `scale(${escala})`, transformOrigin: '0 0', background: '#fff' }} />
      )}
    </div>
  );
}

// Vídeo da aula no editor: link do YouTube OU arquivo enviado (bucket privado inep-videos)
function AulaVideoCampo({ aula, progresso, onMudar, onArquivo }) {
  const [modo, setModo] = useState(aula.videoArquivo ? 'arquivo' : 'youtube');
  const btn = ativo => ({ padding: '4px 9px', fontSize: 11.5, fontWeight: 700, borderRadius: 7, background: ativo ? 'var(--blue)' : 'var(--bg)', color: ativo ? '#fff' : 'var(--muted)' });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <div style={{ display: 'flex', gap: 4 }}>
        <button type="button" style={btn(modo === 'youtube')} onClick={() => setModo('youtube')}>▶ YouTube</button>
        <button type="button" style={btn(modo === 'arquivo')} onClick={() => setModo('arquivo')}>📁 Enviar vídeo</button>
      </div>
      {modo === 'youtube' ? (
        <input value={aula.videoUrl || ''} placeholder="Link do YouTube"
          onChange={e => onMudar({ videoUrl: e.target.value, ...(e.target.value ? { videoArquivo: '', videoNome: '', videoTamanhoMB: null } : {}) })} />
      ) : progresso !== undefined ? (
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>
          Enviando… {progresso}%
          <div className="inep-pbar" style={{ marginTop: 4 }}><i style={{ width: `${progresso}%`, background: 'var(--blue)' }} /></div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, minWidth: 0 }}>
          {aula.videoArquivo && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--mint)' }} title={aula.videoNome}>✓ {aula.videoNome || 'vídeo enviado'}{aula.videoTamanhoMB ? ` (${aula.videoTamanhoMB} MB)` : ''}</span>}
          <label className="inep-add-aula" style={{ cursor: 'pointer', whiteSpace: 'nowrap', margin: 0 }}>
            {aula.videoArquivo ? 'Trocar' : `Escolher arquivo (MP4, até ${LIMITE_VIDEO_MB} MB)`}
            <input type="file" accept="video/mp4,video/webm,video/quicktime" hidden onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onArquivo(f); }} />
          </label>
        </div>
      )}
    </div>
  );
}

// Duração do curso: o campo "carga horária" do curso ou, sem ele, a soma das aulas
function duracaoCurso(curso) {
  if (curso?.cargaHoraria) return `${String(curso.cargaHoraria).replace(/\s*h(oras?)?$/i, '')} hora${Number(curso.cargaHoraria) === 1 ? '' : 's'}`;
  const min = flattenAulas(curso).reduce((a, x) => a + (Number(x.duracaoMin) || 0), 0);
  if (!min) return '';
  return min < 60 ? `${min} min` : `${Math.round(min / 60 * 10) / 10} horas`;
}
function Estrelas({ valor = 0, tamanho = 14, onEscolher }) {
  const [hover, setHover] = useState(0);
  const mostrado = hover || Math.round(valor);
  return (
    <span className="inep-estrelas" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map(n => (
        <span key={n} style={{ fontSize: tamanho, cursor: onEscolher ? 'pointer' : 'default', color: n <= mostrado ? '#f5b301' : 'var(--line)' }}
          onMouseEnter={() => onEscolher && setHover(n)} onClick={e => { e.stopPropagation(); onEscolher?.(n); }}>★</span>
      ))}
    </span>
  );
}
function CourseCard({ curso, pct, onOpen, onVerCertificado, nota, favorito, onFavoritar, onAvaliar }) {
  const cor = CORES_CURSO[curso.cor] || CORES_CURSO.blue;
  const totalAulas = flattenAulas(curso).length;
  const duracao = duracaoCurso(curso);
  const concluido = pct >= 100;
  return (
    <div className="inep-ccard inep-ccard-v2">
      <div className={`inep-ccover ${curso.capaUrl ? '' : cor.css}`} style={curso.capaUrl ? { backgroundImage: `url(${curso.capaUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}>
        {!curso.capaUrl && <><span className="inep-chip">{curso.categoria || 'Curso'}</span><b>{curso.titulo}</b></>}
        {onFavoritar && (
          <button className={`inep-fav ${favorito ? 'inep-fav-on' : ''}`} title={favorito ? 'Tirar dos favoritos' : 'Favoritar'} onClick={e => { e.stopPropagation(); onFavoritar(); }}>{favorito ? '♥' : '♡'}</button>
        )}
      </div>
      <div className="inep-cbody">
        <div className="inep-meta">
          <span>🕒 {duracao || `${totalAulas} aula${totalAulas !== 1 ? 's' : ''}`}</span>
          {nota?.n > 0 && <span title={`${nota.n} avaliação(ões)`}>{nota.media.toFixed(1)} <Estrelas valor={nota.media} tamanho={13} /></span>}
        </div>
        <div className="inep-ctitle">{curso.titulo}</div>
        {pct > 0 && !concluido && <div className="inep-pbar"><i style={{ width: `${pct}%`, background: cor.hex }} /></div>}
        {concluido && onAvaliar && (
          <div className="inep-avaliar">{nota?.minha ? 'Sua avaliação:' : 'Avalie este curso:'} <Estrelas valor={nota?.minha || 0} onEscolher={onAvaliar} /></div>
        )}
        {concluido ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="inep-cbtn" style={{ background: 'var(--bg)', color: 'var(--ink)', border: '1px solid var(--line)' }} onClick={onOpen}>▶ Rever aulas</button>
            <button className="inep-cbtn" style={{ background: '#dcfce7', color: '#15803d' }} onClick={onVerCertificado}><small>JÁ OBTIDO</small> Certificado</button>
          </div>
        ) : (
          <button className="inep-cbtn" style={{ background: 'var(--blue)', color: '#fff' }} onClick={onOpen}>{pct > 0 ? `CONTINUAR · ${pct}%` : 'ACESSAR'}</button>
        )}
      </div>
    </div>
  );
}

// ── Componente principal ─────────────────────────────────────────────────────────
export default function InepEnsino() {
  const navigate = useNavigate();
  const currentUser = auth.currentUser;
  const email = currentUser?.email || '';

  const [theme, setTheme] = useState('light');
  const [sidePinned, setSidePinned] = useState(false);
  const [page, setPage] = useState('dash');
  const [userRoles, setUserRoles] = useState([]);
  const [userDoc, setUserDoc] = useState(null);
  const [meuUserLoaded, setMeuUserLoaded] = useState(false);
  // Aluno só INEP (não tem Quality para voltar) e botão Sair
  const ehSoInep = !!(currentUser?.soInep || userDoc?.soInep);
  const sair = async () => {
    try { await sairDoSistema(auth); } catch (_) { /* segue para o login */ }
    window.location.href = '/login';
  };
  // Nome de quem está usando: cadastro do Quality → nome do login → começo do e-mail
  const userNome = userDoc?.name || currentUser?.displayName || email.split('@')[0] || 'Usuário';
  const primeiroNome = userNome.includes('@') ? userNome : userNome.split(' ')[0];

  const isSuperAdmin = email === 'alexandrebareaoliveira@gmail.com';
  const isAdminMaster = isSuperAdmin || userRoles.includes('admin') || userRoles.includes('administrador');

  useEffect(() => {
    if (!email) return;
    const q = query(collection(db, getCollectionName('users')), where('email', '==', email.toLowerCase()));
    return onSnapshot(q, snap => {
      if (!snap.empty) {
        const u = snap.docs[0].data();
        setUserRoles(Array.isArray(u.roles) ? u.roles : (u.role ? [u.role] : []));
        setUserDoc(u);
      }
      setMeuUserLoaded(true);
    }, () => setMeuUserLoaded(true));
  }, [email]);

  // ── Controle de acesso restrito (admins não-master do INEP) ────────────────
  const [inepAdmins, setInepAdmins] = useState([]);
  useEffect(() => onSnapshot(collection(db, getCollectionName('inep_admins')), snap => {
    setInepAdmins(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }, () => {}), []);
  const meuAcessoRestrito = inepAdmins.find(a => (a.email || '').toLowerCase() === email.toLowerCase());
  const isAdminRestrito = !isAdminMaster && !!meuAcessoRestrito;
  const isAdmin = isAdminMaster || isAdminRestrito;
  const minhasAbasAdmin = isAdminMaster ? ADMIN_TABS.map(t => t.id) : (meuAcessoRestrito?.abas || []);
  const [verComoAluno, setVerComoAluno] = useState(false);

  // Todos os usuários do Quality com acesso liberado ao INEP (aluno e/ou admin) —
  // carregado só quando necessário (admin master abrindo Controle de acesso)
  const [usuariosComAcesso, setUsuariosComAcesso] = useState([]);
  // todos os usuários ativos do Quality — para escolher o gerente da área de cada curso
  const [usuariosQuality, setUsuariosQuality] = useState([]);
  useEffect(() => {
    if (!isAdmin) return;
    return onSnapshot(collection(db, getCollectionName('users')), snap => {
      const todos = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setUsuariosComAcesso(todos.filter(u => u.permissions?.inep_ensino?.view === true));
      setUsuariosQuality(todos.filter(u => u.email && !u.bloqueado).sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email)));
    }, () => {});
  }, [isAdmin]);

  // ── Turmas ──────────────────────────────────────────────────────────────────
  const [turmas, setTurmas] = useState([]);
  useEffect(() => onSnapshot(collection(db, getCollectionName('inep_turmas')), snap => {
    setTurmas(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.nome || '').localeCompare(b.nome || '')));
  }, () => {}), []);
  // Turmas em que eu estou (o banco só devolve as minhas para o aluno só INEP)
  const [minhasTurmas, setMinhasTurmas] = useState(() => new Set());
  useEffect(() => {
    if (!email) return undefined;
    return onSnapshot(query(collection(db, getCollectionName('inep_turma_membros')), where('email', '==', email.toLowerCase())), snap => {
      setMinhasTurmas(new Set(snap.docs.map(d => d.data().turmaId)));
    }, () => {});
  }, [email]);
  // Todos os membros de todas as turmas (só admin; recarregado depois de cada mudança)
  const [membros, setMembros] = useState([]);
  const carregarMembros = async () => {
    const snap = await getDocs(collection(db, getCollectionName('inep_turma_membros')));
    setMembros(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };
  useEffect(() => { if (isAdmin) carregarMembros().catch(() => {}); }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Meu próprio nível de acesso ao INEP — controla se a plataforma libera ou bloqueia
  const temAcessoAluno = userDoc?.permissions?.inep_ensino?.view === true;
  const meuNivelAcesso = isAdminMaster ? 'admin_master' : isAdminRestrito ? 'admin_restrito' : temAcessoAluno ? 'aluno' : null;

  // ── Personalização (tela inicial) ───────────────────────────────────────────
  const [config, setConfig] = useState(null);
  const [configLoaded, setConfigLoaded] = useState(false);
  useEffect(() => onSnapshot(doc(db, getCollectionName('inep_config'), 'geral'), snap => {
    setConfig(snap.exists() ? snap.data() : null);
    setConfigLoaded(true);
  }, () => setConfigLoaded(true)), []);
  // Textos personalizáveis (Personalização → Textos): T('chave', { nome: 'Ana' })
  const T = (chave, vars) => {
    let s = config?.textos?.[chave];
    if (s === undefined || s === null || s === '') s = TEXTOS_PADRAO[chave] ?? chave;
    if (vars) Object.entries(vars).forEach(([k, v]) => { s = s.split(`{${k}}`).join(v); });
    return s;
  };
  const qsValores = config?.quemSomos?.valores?.length ? config.quemSomos.valores : VALORES_PADRAO;
  const qsTimeline = config?.quemSomos?.timeline?.length ? config.quemSomos.timeline : TIMELINE_PADRAO;
  const qsUnidades = config?.quemSomos?.unidades?.length ? config.quemSomos.unidades : UNIDADES_PADRAO;
  const estiloTema = estiloDoTema(config?.cores, theme);
  const heroTitulo = config?.heroTitulo || 'Educação, cuidado e excelência em cada aula.';
  const heroTexto = config?.heroTexto || 'Ambiente virtual de aprendizagem do Grupo Prontobaby. Acompanhe cursos, avaliações, progresso e certificações em um único lugar.';
  const corDestaque = config?.corDestaque || 'blue';
  const [draftConfig, setDraftConfig] = useState(null);
  // Inicializa o rascunho assim que a primeira leitura chegar, mesmo que o documento
  // ainda não exista no banco (nesse caso `config` é null e partimos de um objeto vazio).
  useEffect(() => { if (configLoaded && !draftConfig) setDraftConfig(config || {}); }, [config, configLoaded, draftConfig]);
  const salvarPersonalizacao = async () => {
    await setDoc(doc(db, getCollectionName('inep_config'), 'geral'), { ...draftConfig }, { merge: true });
  };

  // (banner, logo e demais imagens da personalização: inep/InepPersonalizacaoAdmin)

  // (assinaturas do certificado: agora fazem parte do modelo oficial — api/_modelos/)

  // ── Perfil do aluno (avatar próprio, isolado da tabela `users` principal do
  // Quality pra não mexer em nada fora do INEP) ───────────────────────────────
  const [meuPerfil, setMeuPerfil] = useState(null);
  const [perfilCarregado, setPerfilCarregado] = useState(false);
  useEffect(() => {
    if (!email) return;
    return onSnapshot(doc(db, getCollectionName('inep_perfis'), slugEmail(email)), snap => {
      setMeuPerfil(snap.exists() ? snap.data() : null);
      setPerfilCarregado(true);
    }, () => setPerfilCarregado(true));
  }, [email]);

  // ── Presença no INEP (relatórios "online agora" e picos) ──────────────────────
  // A cada minuto, com a aba visível, grava/renova a sessão em inep_acessos.
  const nivelParaPresenca = isAdminMaster || isAdminRestrito || userDoc?.permissions?.inep_ensino?.view === true;
  useEffect(() => {
    if (!email || !nivelParaPresenca) return undefined;
    const sid = `${slugEmail(email)}__${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
    const inicio = new Date().toISOString();
    const ping = () => supabase.from('inep_acessos').upsert({
      id: sid,
      data: { email: email.toLowerCase(), nome: userNome, tipo: isAdmin ? 'admin' : 'aluno', inicio, ultimo: new Date().toISOString() },
    }, { onConflict: 'id' }).then(() => {}, () => {});
    ping();
    const t = setInterval(() => { if (document.visibilityState === 'visible') ping(); }, 60000);
    const aoVoltar = () => { if (document.visibilityState === 'visible') ping(); };
    document.addEventListener('visibilitychange', aoVoltar);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', aoVoltar); };
  }, [email, nivelParaPresenca]); // eslint-disable-line react-hooks/exhaustive-deps

  // Relatórios (admin): quem está online agora e picos diários de acessos simultâneos
  const [onlineAgora, setOnlineAgora] = useState([]);
  const [picos, setPicos] = useState([]);
  const relatoriosAbertos = page === 'relatorios' && isAdmin;
  useEffect(() => {
    if (!relatoriosAbertos) return undefined;
    const carrega = async () => {
      const desde = new Date(Date.now() - 2.5 * 60 * 1000).toISOString();
      const [on, pc] = await Promise.all([
        supabase.from('inep_acessos').select('id,data').gte('data->>ultimo', desde).limit(2000),
        supabase.from('inep_acessos_pico').select('id,data').order('id', { ascending: false }).limit(400),
      ]);
      // uma linha por pessoa (pode ter mais de uma aba aberta)
      const porEmail = {};
      (on.data || []).forEach(r => {
        const d = r.data || {};
        const atual = porEmail[d.email];
        if (!atual || (d.inicio || '') < (atual.inicio || '')) porEmail[d.email] = d;
      });
      setOnlineAgora(Object.values(porEmail).sort((a, b) => (a.nome || '').localeCompare(b.nome || '')));
      setPicos((pc.data || []).map(r => r.data).filter(Boolean));
    };
    carrega().catch(() => {});
    const t = setInterval(() => carrega().catch(() => {}), 30000);
    return () => clearInterval(t);
  }, [relatoriosAbertos]);

  // Admin abrindo o perfil de um aluno (lista de alunos)
  const [perfilAluno, setPerfilAluno] = useState(null); // { email, nome, userDoc, perfil }
  const abrirPerfilAluno = async (alunoEmail, alunoNome) => {
    const e = (alunoEmail || '').toLowerCase();
    const snap = await getDoc(doc(db, getCollectionName('inep_perfis'), slugEmail(e))).catch(() => null);
    const u = usuariosComAcesso.find(x => (x.email || '').toLowerCase() === e) || null;
    setPerfilAluno({ email: e, nome: u?.name || alunoNome || e, userDoc: u, perfil: snap?.exists?.() ? snap.data() : {} });
  };

  // Favoritos (no próprio perfil) e estrelas dos cursos (inep_estrelas: um voto por aluno/curso)
  const favoritos = useMemo(() => new Set(meuPerfil?.favoritos || []), [meuPerfil]);
  const alternarFavorito = async (cursoId) => {
    const novos = new Set(favoritos);
    if (novos.has(cursoId)) novos.delete(cursoId); else novos.add(cursoId);
    await setDoc(doc(db, getCollectionName('inep_perfis'), slugEmail(email)), { favoritos: [...novos], email: email.toLowerCase() }, { merge: true });
  };
  const [estrelas, setEstrelas] = useState([]);
  useEffect(() => onSnapshot(collection(db, getCollectionName('inep_estrelas')), snap => {
    setEstrelas(snap.docs.map(d => d.data()));
  }, () => {}), []);
  const notaCurso = useMemo(() => {
    const m = {};
    estrelas.forEach(e => {
      const x = (m[e.cursoId] = m[e.cursoId] || { soma: 0, n: 0, minha: 0 });
      x.soma += Number(e.estrelas) || 0; x.n += 1;
      if ((e.email || '').toLowerCase() === email.toLowerCase()) x.minha = Number(e.estrelas) || 0;
    });
    Object.values(m).forEach(x => { x.media = x.n ? x.soma / x.n : 0; });
    return m;
  }, [estrelas, email]);
  const avaliarCurso = async (cursoId, n) => {
    await setDoc(doc(db, getCollectionName('inep_estrelas'), `${cursoId}__${slugEmail(email)}`), {
      cursoId, email: email.toLowerCase(), estrelas: n, criadoEm: serverTimestamp(),
    });
  };
  const [filtroCatalogo, setFiltroCatalogo] = useState('todos');
  const [buscaCatalogo, setBuscaCatalogo] = useState('');
  const [buscaAlunos, setBuscaAlunos] = useState('');
  // (foto e capa do perfil agora são enviadas pelo componente inep/InepPerfil)
  const [perfilTab, setPerfilTab] = useState('dados');

  // ── Dados: cursos, matrículas, certificados, chat ───────────────────────────
  // todosCursos: inclusive desativados (tela do admin). cursos: o que a pessoa enxerga —
  // admin vê todos os ativos; aluno (ou "ver como aluno") só os das suas turmas.
  // Cursos excluídos vão para a lixeira (excluido: true): somem de todas as telas, mas ficam guardados
  const [todosCursos, setTodosCursos] = useState([]);
  const [cursosNaLixeira, setCursosNaLixeira] = useState([]);
  useEffect(() => {
    return onSnapshot(collection(db, getCollectionName('inep_cursos')), snap => {
      const lista = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setTodosCursos(lista.filter(c => !c.excluido));
      setCursosNaLixeira(lista.filter(c => c.excluido));
    }, () => {});
  }, []);
  const modoAdmin = isAdmin && !verComoAluno;
  const cursos = useMemo(
    // Aluno só vê curso com avaliação final (sem ela não há média para o certificado oficial)
    () => todosCursos.filter(c => c.ativo !== false && (modoAdmin || (cursoVisivelParaTurmas(c, minhasTurmas) && c.avaliacao?.perguntas?.length > 0))),
    [todosCursos, modoAdmin, minhasTurmas]
  );

  const [todasMatriculas, setTodasMatriculas] = useState([]);
  useEffect(() => {
    return onSnapshot(collection(db, getCollectionName('inep_matriculas')), snap => {
      // aulasFeitas sempre como lista (registros antigos podiam ter { _type: 'arrayUnion', items })
      setTodasMatriculas(snap.docs.map(d => {
        const m = { id: d.id, ...d.data() };
        const af = m.aulasFeitas;
        return { ...m, aulasFeitas: Array.isArray(af) ? af : (Array.isArray(af?.items) ? af.items : []) };
      }));
    }, () => {});
  }, []);
  const minhasMatriculas = useMemo(() => {
    const map = {};
    todasMatriculas.filter(m => m.userEmail === email).forEach(m => { map[m.cursoId] = m; });
    return map;
  }, [todasMatriculas, email]);

  const [certificados, setCertificados] = useState([]);
  useEffect(() => {
    return onSnapshot(collection(db, getCollectionName('inep_certificados')), snap => {
      setCertificados(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, () => {});
  }, []);
  const meusCertificados = useMemo(() => certificados.filter(c => c.userEmail === email), [certificados, email]);
  const [certFlipped, setCertFlipped] = useState({}); // { [certId]: boolean } — frente/verso

  // ── Lesson player ────────────────────────────────────────────────────────────
  const [lessonCursoId, setLessonCursoId] = useState(null);
  const [lessonAulaId, setLessonAulaId] = useState(null);
  const abrirCurso = (cursoId) => {
    const curso = cursos.find(c => c.id === cursoId);
    const aulas = flattenAulas(curso);
    const mat = minhasMatriculas[cursoId];
    const feitas = new Set(mat?.aulasFeitas || []);
    const proxima = aulas.find(a => !feitas.has(a.id)) || aulas[0];
    setLessonCursoId(cursoId);
    setLessonAulaId(proxima?.id || null);
    setPage('lesson');
  };
  const lessonCurso = cursos.find(c => c.id === lessonCursoId);
  const lessonAulas = useMemo(() => flattenAulas(lessonCurso), [lessonCurso]);
  const lessonAula = lessonAulas.find(a => a.id === lessonAulaId);
  const lessonMat = minhasMatriculas[lessonCursoId];
  const lessonFeitas = new Set(lessonMat?.aulasFeitas || []);

  // Chamadas ao servidor do INEP (prova corrigida e certificado emitido/congelado lá)
  const chamarServidorInep = async (rota, corpo) => {
    const { data: { session } } = await supabase.auth.getSession();
    const r = await fetch(rota, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` }, body: JSON.stringify(corpo) });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || `Erro ${r.status}`);
    return j;
  };

  // ── Certificado oficial em PDF (modelo do pacote "certificado-inep") ─────────
  const [certPedirDados, setCertPedirDados] = useState(null); // { cert, nome, cpf, erro }
  const [gerandoCert, setGerandoCert] = useState('');
  // Dados do certificado oficial (os mesmos na prévia da tela e no PDF). Depois do
  // primeiro download ficam congelados em cert.oficial.
  const montarOficial = (cert, nomeCompleto, cpf) => {
    if (cert.oficial) return cert.oficial;
    // inclui a lixeira: certificado de curso excluído continua saindo completo
    const curso = [...todosCursos, ...cursosNaLixeira].find(c => c.id === cert.cursoId) || {};
    const mat = minhasMatriculas[cert.cursoId] || {};
    const rod = { ...RODAPE_PADRAO, ...(config?.rodape || {}) };
    return {
        alunoNome: nomeProprio(nomeCompleto),
        cpf: formatarCpf(cpf),
        cursoTitulo: curso.titulo || cert.cursoTitulo,
        responsavel: curso.responsavel || 'Coordenação de Ensino do INEP',
        notaMinima: curso.notaMinima ? Number(String(curso.notaMinima).replace(',', '.')).toFixed(1).replace('.', ',') : (curso.avaliacao?.perguntas?.length ? '7,0' : 'Não se aplica'),
        modulos: (curso.modulos || []).map(m => m.titulo).filter(Boolean),
        inicio: dataBR(mat.criadoEm || cert.emitidoEm),
        fim: dataBR(mat.concluidoEm || cert.emitidoEm),
        cargaHoraria: duracaoCurso(curso) || '—',
        frequencia: `${progressoCurso(curso, mat) || 100}%`,
        nota: notaBR(mat.notaAvaliacao),
        dataEmissao: dataExtenso(cert.emitidoEm || new Date()),
        instituicao: T('cert.instituicao'),
        cnpj: rod.cnpj || '—',
        cidade: T('cert.cidade'),
    };
  };
  const gerarCertificadoOficial = async (janela, cert, nomeCompleto, cpf) => {
    setGerandoCert(cert.id);
    try {
      // O servidor congela os dados no primeiro download e devolve sempre os mesmos
      const oficial = await chamarServidorInep('/api/inep-certificado', { certId: cert.id, nomeCompleto, cpf });
      const html = await montarHtml({
        aluno_nome: oficial.alunoNome, aluno_cpf: oficial.cpf, aluno_frequencia: oficial.frequencia,
        curso_titulo: oficial.cursoTitulo, curso_responsavel: oficial.responsavel, curso_nota_minima: oficial.notaMinima,
        curso_modulos: oficial.modulos, certificado_dia_inicio: oficial.inicio, certificado_dia_fim: oficial.fim,
        certificado_carga_horaria: oficial.cargaHoraria, certificado_nota: oficial.nota,
        certificado_data_emissao: oficial.dataEmissao, certificado_validacao_url: urlValidacao(oficial.codigo || cert.codigo),
        cod: oficial.codigo || cert.codigo, ead_nome: oficial.instituicao, ead_cnpj: oficial.cnpj, ead_cidade: oficial.cidade,
      });
      await imprimirNaJanela(janela, html);
    } catch (e) {
      try { janela?.close(); } catch (_) { /* já fechada */ }
      alert('Não foi possível gerar o certificado: ' + e.message);
    } finally {
      setGerandoCert('');
    }
  };
  // Admin: PDF de exemplo do modelo oficial
  const verExemploPdf = () => {
    const janela = abrirJanelaCertificado();
    montarHtml(EXEMPLO_CERT).then(h => imprimirNaJanela(janela, h)).catch(e => { try { janela?.close(); } catch (_) { /* fechada */ } alert(e.message); });
  };
  // Clique em "Baixar certificado": precisa de nome completo e CPF válido (fica no perfil)
  const baixarCertificado = (cert) => {
    const nome = (meuPerfil?.nomeCompleto || '').trim();
    const cpf = meuPerfil?.documento || '';
    if (!cert.oficial && (nome.split(/\s+/).length < 2 || !cpfValido(cpf))) {
      setCertPedirDados({ cert, nome: nome || userNome, cpf, erro: '' });
      return;
    }
    gerarCertificadoOficial(abrirJanelaCertificado(), cert, nome, cpf);
  };
  const confirmarDadosCertificado = () => {
    const { cert, nome, cpf } = certPedirDados;
    if (nome.trim().split(/\s+/).length < 2) { setCertPedirDados(d => ({ ...d, erro: 'Informe o nome completo (nome e sobrenome).' })); return; }
    if (!cpfValido(cpf)) { setCertPedirDados(d => ({ ...d, erro: 'CPF inválido — confira os números.' })); return; }
    const janela = abrirJanelaCertificado(); // abre já no clique (senão o navegador bloqueia)
    setDoc(doc(db, getCollectionName('inep_perfis'), slugEmail(email)), { nomeCompleto: nomeProprio(nome), documento: formatarCpf(cpf), email: email.toLowerCase() }, { merge: true }).catch(() => {});
    setCertPedirDados(null);
    gerarCertificadoOficial(janela, cert, nome, cpf);
  };

  const marcarAulaConcluida = async () => {
    if (!lessonCurso || !lessonAula) return;
    const mid = matriculaId(lessonCurso.id, email);
    const jaExiste = !!minhasMatriculas[lessonCurso.id];
    await setDoc(doc(db, getCollectionName('inep_matriculas'), mid), {
      ...(jaExiste ? {} : { criadoEm: serverTimestamp() }),
      userEmail: email, userNome, cursoId: lessonCurso.id,
      aulasFeitas: arrayUnion(lessonAula.id),
      atualizadoEm: serverTimestamp(),
    }, { merge: true });
    const idx = lessonAulas.findIndex(a => a.id === lessonAula.id);
    const proxima = lessonAulas[idx + 1];
    if (proxima) {
      setLessonAulaId(proxima.id);
    } else if (lessonCurso.avaliacao?.perguntas?.length) {
      // Última aula: vai para a avaliação (a conclusão e o certificado saem do servidor)
      setExamCursoId(lessonCurso.id);
      setExamRespostas({});
      setExamResultado(null);
      setPage('avaliacoes');
    }
  };

  // Propriedades do card de curso (início e catálogo)
  const propsCard = (c) => ({
    curso: c,
    pct: progressoCurso(c, minhasMatriculas[c.id]),
    onOpen: () => abrirCurso(c.id),
    onVerCertificado: () => setPage('certs'),
    nota: notaCurso[c.id],
    favorito: favoritos.has(c.id),
    onFavoritar: () => alternarFavorito(c.id),
    onAvaliar: (n) => avaliarCurso(c.id, n),
  });

  // ── "Continuar aula" — resolve automaticamente qual curso/aula abrir ────────
  const cursoContinuar = useMemo(() => {
    const comProgresso = cursos
      .map(c => ({ c, mat: minhasMatriculas[c.id], pct: progressoCurso(c, minhasMatriculas[c.id]) }))
      .filter(x => x.pct > 0 && x.pct < 100)
      .sort((a, b) => tsVal(b.mat?.atualizadoEm) - tsVal(a.mat?.atualizadoEm));
    if (comProgresso[0]) return comProgresso[0].c;
    return cursos.find(c => progressoCurso(c, minhasMatriculas[c.id]) === 0) || null;
  }, [cursos, minhasMatriculas]);
  const handleContinuarAula = () => {
    if (cursoContinuar) abrirCurso(cursoContinuar.id);
    else setPage('continuar_vazio');
  };

  // ── Avaliações ────────────────────────────────────────────────────────────────
  const [examCursoId, setExamCursoId] = useState(null);
  const [examRespostas, setExamRespostas] = useState({});
  const [examResultado, setExamResultado] = useState(null);
  const cursosComExame = cursos.filter(c => c.avaliacao?.perguntas?.length > 0);
  const examCurso = cursos.find(c => c.id === examCursoId);

  // A prova é corrigida no servidor (o gabarito não chega ao navegador do aluno)
  const [enviandoProva, setEnviandoProva] = useState(false);
  const submeterAvaliacao = async () => {
    if (!examCurso || enviandoProva) return;
    setEnviandoProva(true);
    try {
      const r = await chamarServidorInep('/api/inep-avaliacao', { cursoId: examCurso.id, respostas: examRespostas });
      setExamResultado({ nota: r.nota, passou: r.passou });
    } catch (e) {
      alert('Não foi possível enviar a avaliação: ' + e.message);
    } finally {
      setEnviandoProva(false);
    }
  };

  // ── Chat + chamada (Jitsi real, mesmo padrão da Intranet) ──────────────────────
  const [salaId, setSalaId] = useState('geral');
  const [chatText, setChatText] = useState('');
  const [emChamada, setEmChamada] = useState(false);
  const salas = useMemo(() => ([
    { id: 'geral', nome: '💬 Mural Geral', cor: 'var(--blue)' },
    ...cursos.map(c => ({ id: c.id, nome: c.titulo, cor: (CORES_CURSO[c.cor] || CORES_CURSO.blue).hex })),
  ]), [cursos]);
  const [chatMsgs, setChatMsgs] = useState([]);
  useEffect(() => {
    setEmChamada(false);
    const q = query(collection(db, getCollectionName('inep_chat_mensagens')), where('salaId', '==', salaId));
    return onSnapshot(q, snap => {
      const msgs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      msgs.sort((a, b) => tsVal(a.criadoEm) - tsVal(b.criadoEm));
      setChatMsgs(msgs);
    }, () => {});
  }, [salaId]);
  const enviarMsg = async () => {
    if (!chatText.trim()) return;
    await addDoc(collection(db, getCollectionName('inep_chat_mensagens')), {
      salaId, autorEmail: email, autorNome: userNome, texto: chatText.trim(), criadoEm: serverTimestamp(),
    });
    setChatText('');
  };
  const jitsiUrl = `https://meet.jit.si/iw-inep-${salaId}`;

  function ChatPanel() {
    return (
      <div className="inep-chat-wrap">
        <div className="inep-chat-mods-col">
          <div className="inep-chat-mods">
            {salas.map(s => (
              <button key={s.id} className={`inep-chat-mod-btn ${salaId === s.id ? 'inep-sel' : ''}`} onClick={() => setSalaId(s.id)}>
                <span className="inep-sw" style={{ background: s.cor }} />
                <span>{s.nome}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="inep-chat-main">
          <div className="inep-chat-head">
            <span className="inep-sw" style={{ background: salas.find(s => s.id === salaId)?.cor, width: 10, height: 10, borderRadius: 3, display: 'inline-block' }} />
            <div><b>{salas.find(s => s.id === salaId)?.nome}</b><small>{chatMsgs.length} mensagem(ns)</small></div>
            <div className="inep-chat-head-actions">
              <button className="inep-call-btn" title="Iniciar chamada em grupo" onClick={() => setEmChamada(v => !v)}>📹</button>
            </div>
          </div>
          {emChamada ? (
            <iframe src={jitsiUrl} title="Chamada INEP" allow="camera; microphone; fullscreen; display-capture; autoplay" style={{ flex: 1, border: 0 }} />
          ) : (
            <>
              <div className="inep-chat-body">
                {chatMsgs.length === 0 && <div style={{ color: 'var(--muted)', textAlign: 'center', padding: 20 }}>Nenhuma mensagem ainda — seja o primeiro a escrever.</div>}
                {chatMsgs.map(m => (
                  <div key={m.id} className={`inep-msg-row ${m.autorEmail === email ? 'inep-out' : ''}`}>
                    <div className={`inep-msg ${m.autorEmail === email ? 'inep-out' : 'inep-in'}`}>
                      {m.autorEmail !== email && <b style={{ display: 'block', fontSize: 11, opacity: .7 }}>{m.autorNome}</b>}
                      {m.texto}
                      <small>{fmtData(m.criadoEm)}</small>
                    </div>
                  </div>
                ))}
              </div>
              <div className="inep-chat-input">
                <input value={chatText} onChange={e => setChatText(e.target.value)} placeholder="Escreva uma mensagem…" onKeyDown={e => e.key === 'Enter' && enviarMsg()} />
                <button onClick={enviarMsg}>Enviar</button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Estatísticas do aluno pra emblemas (tudo derivado de dados já carregados,
  // sem nenhuma busca nova) ────────────────────────────────────────────────────
  const minhasStats = useMemo(() => {
    const minhasNotas = todasMatriculas.filter(m => m.userEmail === email && typeof m.notaAvaliacao === 'number').map(m => m.notaAvaliacao);
    const aulasFeitas = Object.values(minhasMatriculas).reduce((tot, m) => tot + (m.aulasFeitas || []).length, 0);
    // "mensagensEnviadas" só reflete a sala de chat atualmente carregada (evita abrir
    // uma nova assinatura só pra esse emblema — ver ChatPanel/chatMsgs mais abaixo).
    return {
      aulasFeitas,
      cursosConcluidos: meusCertificados.length,
      notas: minhasNotas,
      notaMax: minhasNotas.length ? Math.max(...minhasNotas) : 0,
      mediaNotas: minhasNotas.length ? minhasNotas.reduce((a, b) => a + b, 0) / minhasNotas.length : 0,
      mensagensEnviadas: chatMsgs.filter(m => m.autorEmail === email).length,
    };
  }, [todasMatriculas, minhasMatriculas, meusCertificados, email, chatMsgs]);
  const meusEmblemas = useMemo(() => BADGES.map(b => ({ ...b, unlocked: b.check(minhasStats) })), [minhasStats]);

  // ── KPIs do dashboard do aluno ────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const progresos = cursos.map(c => progressoCurso(c, minhasMatriculas[c.id]));
    const emAndamento = cursos.filter(c => { const p = progressoCurso(c, minhasMatriculas[c.id]); return p > 0 && p < 100; }).length;
    const geral = progresos.length ? Math.round(progresos.reduce((a, b) => a + b, 0) / progresos.length) : 0;
    const notas = todasMatriculas.filter(m => m.userEmail === email && typeof m.notaAvaliacao === 'number').map(m => m.notaAvaliacao);
    const media = notas.length ? (notas.reduce((a, b) => a + b, 0) / notas.length).toFixed(1) : '—';
    return { emAndamento, total: cursos.length, geral, media, certificados: meusCertificados.length };
  }, [cursos, minhasMatriculas, todasMatriculas, email, meusCertificados]);

  // ── Jogo educativo: "Segurança em Ação" ─────────────────────────────────────
  const [gameRunning, setGameRunning] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [playerLane, setPlayerLane] = useState(1);
  const [hazards, setHazards] = useState([]);
  const playerLaneRef = useRef(1);
  useEffect(() => { playerLaneRef.current = playerLane; }, [playerLane]);
  const startGame = () => { setScore(0); setLives(3); setHazards([]); setPlayerLane(1); setGameOver(false); setGameRunning(true); };
  useEffect(() => {
    if (!gameRunning) return;
    const tick = setInterval(() => {
      setHazards(hs => {
        const moved = hs.map(h => ({ ...h, y: h.y + 7 }));
        let lostLife = false;
        const kept = moved.filter(h => {
          if (h.y >= 86 && h.y < 96 && h.lane === playerLaneRef.current && !h._hit) { lostLife = true; h._hit = true; }
          return h.y < 100;
        });
        if (lostLife) {
          setLives(l => { const nl = l - 1; if (nl <= 0) { setGameRunning(false); setGameOver(true); } return nl; });
        }
        return kept;
      });
      setScore(s => s + 1);
      if (Math.random() < 0.3) {
        setHazards(hs => [...hs, { id: Date.now() + Math.random(), lane: Math.floor(Math.random() * 3), y: 0 }]);
      }
    }, 160);
    return () => clearInterval(tick);
  }, [gameRunning]);
  const moverJogador = (delta) => setPlayerLane(l => Math.max(0, Math.min(2, l + delta)));

  // ── Admin: editor de curso ────────────────────────────────────────────────────
  const [editingCurso, setEditingCurso] = useState(null);
  const [enviandoCapaCurso, setEnviandoCapaCurso] = useState(false);
  // Envio de vídeo próprio de uma aula (progresso por aula: { [aulaId]: 0–100 | 'erro' })
  const [progressoVideo, setProgressoVideo] = useState({});
  const enviarVideoDaAula = async (mi, ai, aulaId, file) => {
    if (!file) return;
    const mudar = campos => setEditingCurso(c => {
      if (!c) return c;
      const modulos = [...c.modulos]; const aulas = [...modulos[mi].aulas];
      const idx = aulas.findIndex(x => x.id === aulaId); if (idx < 0) return c;
      aulas[idx] = { ...aulas[idx], ...campos }; modulos[mi] = { ...modulos[mi], aulas };
      return { ...c, modulos };
    });
    try {
      const minutos = await duracaoDoArquivo(file);
      setProgressoVideo(p => ({ ...p, [aulaId]: 0 }));
      const ref = await enviarVideo(file, pct => setProgressoVideo(p => ({ ...p, [aulaId]: pct })));
      mudar({ videoArquivo: ref, videoNome: file.name, videoTamanhoMB: Math.round(file.size / 104857.6) / 10, videoUrl: '', ...(minutos ? { duracaoMin: String(minutos) } : {}) });
      setProgressoVideo(p => { const n = { ...p }; delete n[aulaId]; return n; });
    } catch (e) {
      setProgressoVideo(p => { const n = { ...p }; delete n[aulaId]; return n; });
      alert(e.message);
    }
  };
  const novoModulo = () => ({ id: 'm' + Date.now(), titulo: '', aulas: [] });
  const novaAula = () => ({ id: 'a' + Date.now() + Math.random().toString(36).slice(2, 6), titulo: '', videoUrl: '', duracaoMin: '' });
  const abrirNovoCurso = () => setEditingCurso({ titulo: '', descricao: '', cor: 'blue', categoria: '', modulos: [novoModulo()], avaliacao: { perguntas: [] }, ativo: true, visibilidade: 'turmas', turmaIds: [] });
  // O gabarito (alternativa certa) fica em inep_gabaritos, que o aluno não lê;
  // o curso guarda só as perguntas e opções.
  const abrirEditorCurso = async (curso) => {
    setEditingCurso({ ...curso });
    try {
      const g = await getDoc(doc(db, getCollectionName('inep_gabaritos'), curso.id));
      const respostas = g.exists() ? (g.data().respostas || []) : [];
      setEditingCurso(c => (c && c.id === curso.id ? {
        ...c,
        avaliacao: { ...(c.avaliacao || {}), perguntas: (c.avaliacao?.perguntas || []).map((p, i) => ({ ...p, correta: p.correta ?? respostas[i] ?? 0 })) },
      } : c));
    } catch (e) {
      console.warn('[INEP] gabarito', e.message);
    }
  };
  const salvarCurso = async () => {
    if (!editingCurso.titulo?.trim()) return;
    const { id, ...data } = editingCurso;
    const perguntas = data.avaliacao?.perguntas || [];
    const semGabarito = { ...data, avaliacao: { ...(data.avaliacao || {}), perguntas: perguntas.map(({ correta, ...p }) => p) } };
    let cursoId = id;
    if (id) await updateDoc(doc(db, getCollectionName('inep_cursos'), id), semGabarito);
    else cursoId = (await addDoc(collection(db, getCollectionName('inep_cursos')), { ...semGabarito, criadoEm: serverTimestamp() })).id;
    await setDoc(doc(db, getCollectionName('inep_gabaritos'), cursoId), { respostas: perguntas.map(p => Number(p.correta) || 0), atualizadoEm: serverTimestamp() });
    setEditingCurso(null);
  };
  // Atalho "Lançar avaliação": escolhe o curso e abre o editor já rolado até as perguntas
  const [escolherCursoAvaliacao, setEscolherCursoAvaliacao] = useState(false);
  const [scrollParaAvaliacao, setScrollParaAvaliacao] = useState(false);
  const avaliacaoSecRef = useRef(null);
  useEffect(() => {
    if (!editingCurso || !scrollParaAvaliacao) return;
    const t = setTimeout(() => {
      avaliacaoSecRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setScrollParaAvaliacao(false);
    }, 60);
    return () => clearTimeout(t);
  }, [editingCurso, scrollParaAvaliacao]);
  const abrirAvaliacaoDoCurso = (curso) => {
    abrirEditorCurso(curso);
    setEscolherCursoAvaliacao(false);
    setScrollParaAvaliacao(true);
    setPage('cursos_admin');
  };
  const desativarCurso = async (curso) => {
    await updateDoc(doc(db, getCollectionName('inep_cursos'), curso.id), { ativo: !(curso.ativo !== false) });
  };
  const [verLixeira, setVerLixeira] = useState(false);
  const excluirCurso = async (curso) => {
    const alunos = todasMatriculas.filter(m => m.cursoId === curso.id).length;
    if (!window.confirm(`Excluir o curso "${curso.titulo}"?

Ele some de todas as telas e vai para a Lixeira (em Cursos & módulos), de onde pode ser restaurado.` +
      (alunos ? `

${alunos} aluno(s) já começaram este curso: as notas e os certificados emitidos continuam guardados e válidos.` : ''))) return;
    await updateDoc(doc(db, getCollectionName('inep_cursos'), curso.id), { excluido: true, excluidoEm: new Date().toISOString(), excluidoPor: email });
  };
  const restaurarCurso = async (curso) => {
    await updateDoc(doc(db, getCollectionName('inep_cursos'), curso.id), { excluido: false, restauradoEm: new Date().toISOString(), restauradoPor: email });
  };

  // ── Admin: acompanhamento dos colaboradores novos (api/inep-acompanhamento) ──
  const [acomp, setAcomp] = useState(null); // { config, estado, hoje, situacao }
  const [acompErro, setAcompErro] = useState('');
  const [acompCfg, setAcompCfg] = useState(null); // formulário { ligado, rhEmails (texto), diasAntes }
  const [acompOcupado, setAcompOcupado] = useState('');
  const [acompSoPendentes, setAcompSoPendentes] = useState(false);
  const [acompPrevia, setAcompPrevia] = useState(null); // e-mails que sairiam agora
  const lerAcompanhamento = async () => {
    setAcompErro('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/inep-acompanhamento', { headers: { Authorization: `Bearer ${session?.access_token}` } });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `Erro ${r.status}`);
      setAcomp(j);
      setAcompCfg({ ligado: j.config.ligado, rhEmails: j.config.rhEmails.join(', '), diasAntes: j.config.diasAntes });
    } catch (e) {
      setAcompErro(e.message);
    }
  };
  useEffect(() => { if (page === 'acompanhamento' && isAdmin) lerAcompanhamento(); }, [page, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps
  const acompAlterado = !!(acomp && acompCfg) && (acompCfg.ligado !== acomp.config.ligado || Number(acompCfg.diasAntes) !== acomp.config.diasAntes
    || acompCfg.rhEmails.split(/[\s,;]+/).filter(Boolean).join(',').toLowerCase() !== acomp.config.rhEmails.join(','));
  const acaoAcompanhamento = async (acao, rotulo) => {
    setAcompOcupado(rotulo);
    setAcompErro('');
    try {
      // coleção própria (fora do inep_config, que os alunos de fora leem); criada na 1ª vez por quem está logado
      await supabase.rpc('create_collection_table', { p_table: 'inep_acompanhamento' });
      // prévia e envio usam a configuração gravada: grava antes o que foi mudado na tela
      if (acao !== 'config' && acompAlterado) await chamarServidorInep('/api/inep-acompanhamento', { acao: 'config', config: acompCfg });
      return await chamarServidorInep('/api/inep-acompanhamento', { acao, config: acompCfg });
    } catch (e) {
      setAcompErro(e.message);
      return null;
    } finally {
      setAcompOcupado('');
    }
  };
  const salvarCfgAcomp = async () => { if (await acaoAcompanhamento('config', 'Salvando…')) await lerAcompanhamento(); };
  const verPreviaAcomp = async () => {
    const r = await acaoAcompanhamento('previa', 'Montando a prévia…');
    if (r) { setAcompPrevia(r.emails); if (acompAlterado) lerAcompanhamento(); }
  };
  const enviarAgoraAcomp = async () => {
    if (!window.confirm('Enviar agora os resumos para o RH e os gerentes?\n\nConta como o envio de hoje: o automático das 7h só volta no próximo dia útil.')) return;
    const r = await acaoAcompanhamento('enviar', 'Enviando…');
    if (r) {
      alert(`${r.enviados.length} e-mail(s) enviado(s).${r.erros.length ? `\n${r.erros.length} com erro: ${r.erros.map(x => x.erro).join('; ')}` : ''}${!r.enviados.length && !r.erros.length ? '\nNão havia nada para avisar.' : ''}`);
      await lerAcompanhamento();
    }
  };

  // ── Admin: turmas ────────────────────────────────────────────────────────────
  const [turmaSelId, setTurmaSelId] = useState(null);
  const [turmaForm, setTurmaForm] = useState(null); // { id?, nome, descricao }
  const [buscaMembro, setBuscaMembro] = useState('');
  const [buscaAdd, setBuscaAdd] = useState('');
  const [filtroTipoAdd, setFiltroTipoAdd] = useState('');
  const [filtroCoopAdd, setFiltroCoopAdd] = useState('');
  const [selAdd, setSelAdd] = useState(() => new Set());
  const [salvandoTurma, setSalvandoTurma] = useState('');
  const turmaSel = turmas.find(t => t.id === turmaSelId) || null;
  const membrosPorTurma = useMemo(() => {
    const m = {};
    membros.forEach(x => { (m[x.turmaId] = m[x.turmaId] || []).push(x); });
    return m;
  }, [membros]);
  const cursosDaTurma = id => todosCursos.filter(c => c.visibilidade === 'turmas' && (c.turmaIds || []).includes(id));

  const salvarTurma = async () => {
    const nome = turmaForm?.nome?.trim();
    if (!nome) return;
    if (turmaForm.id) {
      await updateDoc(doc(db, getCollectionName('inep_turmas'), turmaForm.id), { nome, descricao: turmaForm.descricao || '' });
    } else {
      const ref = await addDoc(collection(db, getCollectionName('inep_turmas')), { nome, descricao: turmaForm.descricao || '', criadoEm: serverTimestamp() });
      setTurmaSelId(ref.id);
    }
    setTurmaForm(null);
  };
  const excluirTurma = async (t) => {
    const n = (membrosPorTurma[t.id] || []).length;
    if (!window.confirm(`Excluir a turma "${t.nome}"?\n\n${n} aluno(s) saem dela e os cursos deixam de estar vinculados a ela. Os alunos e os cursos continuam existindo.`)) return;
    const ids = (membrosPorTurma[t.id] || []).map(x => x.id);
    for (let i = 0; i < ids.length; i += 500) await supabase.from('inep_turma_membros').delete().in('id', ids.slice(i, i + 500));
    for (const c of cursosDaTurma(t.id)) {
      await updateDoc(doc(db, getCollectionName('inep_cursos'), c.id), { turmaIds: (c.turmaIds || []).filter(x => x !== t.id) });
    }
    await deleteDoc(doc(db, getCollectionName('inep_turmas'), t.id));
    setTurmaSelId(null);
    await carregarMembros();
  };
  // Liga/desliga um curso desta turma (o vínculo fica no próprio curso: turmaIds)
  const alternarCursoNaTurma = async (curso, turmaId) => {
    const ids = new Set(curso.turmaIds || []);
    if (ids.has(turmaId)) ids.delete(turmaId); else ids.add(turmaId);
    await updateDoc(doc(db, getCollectionName('inep_cursos'), curso.id), { turmaIds: [...ids], visibilidade: 'turmas' });
  };
  const adicionarMembros = async (pessoas) => {
    if (!turmaSel || !pessoas.length) return;
    setSalvandoTurma(`Adicionando ${pessoas.length} aluno(s)…`);
    try {
      const linhas = pessoas.map(p => ({
        id: membroId(turmaSel.id, p.email),
        data: { turmaId: turmaSel.id, email: (p.email || '').toLowerCase(), nome: p.name || p.email, adicionadoEm: new Date().toISOString(), adicionadoPor: email },
      }));
      for (let i = 0; i < linhas.length; i += 500) {
        const { error } = await supabase.from('inep_turma_membros').upsert(linhas.slice(i, i + 500), { onConflict: 'id' });
        if (error) throw error;
      }
      setSelAdd(new Set());
      await carregarMembros();
    } catch (e) {
      alert('Erro ao adicionar: ' + e.message);
    } finally {
      setSalvandoTurma('');
    }
  };
  const removerMembro = async (m) => {
    await supabase.from('inep_turma_membros').delete().eq('id', m.id);
    await carregarMembros();
  };

  // ── Admin: agregados por aluno, relatórios ──────────────────────────────────
  const alunosAgg = useMemo(() => {
    const map = {};
    todasMatriculas.forEach(m => {
      if (!map[m.userEmail]) map[m.userEmail] = { email: m.userEmail, nome: m.userNome || m.userEmail, cursosNomes: [], progresos: [], notas: [], primeiroAcesso: null };
      const s = map[m.userEmail];
      const curso = cursos.find(c => c.id === m.cursoId);
      if (curso) s.cursosNomes.push(curso.titulo);
      s.progresos.push(curso ? progressoCurso(curso, m) : 0);
      if (typeof m.notaAvaliacao === 'number') s.notas.push(m.notaAvaliacao);
      const t = tsVal(m.criadoEm || m.atualizadoEm);
      if (t && (!s.primeiroAcesso || t < s.primeiroAcesso)) s.primeiroAcesso = t;
    });
    certificados.forEach(c => { if (map[c.userEmail]) { map[c.userEmail].certificadosCount = (map[c.userEmail].certificadosCount || 0) + 1; } });
    return Object.values(map).map(s => ({
      ...s,
      progressoMedio: s.progresos.length ? Math.round(s.progresos.reduce((a, b) => a + b, 0) / s.progresos.length) : 0,
      mediaGeral: s.notas.length ? (s.notas.reduce((a, b) => a + b, 0) / s.notas.length).toFixed(1) : '—',
      certificadosCount: s.certificadosCount || 0,
    })).sort((a, b) => b.progressoMedio - a.progressoMedio);
  }, [todasMatriculas, cursos, certificados]);

  const conclusaoPorCurso = useMemo(() => cursos.map(c => {
    const mats = todasMatriculas.filter(m => m.cursoId === c.id);
    const pcts = mats.map(m => progressoCurso(c, m));
    return { titulo: c.titulo, pct: pcts.length ? Math.round(pcts.reduce((a, b) => a + b, 0) / pcts.length) : 0 };
  }), [cursos, todasMatriculas]);

  const exportRelatorioGeral = () => downloadCsv('inep_relatorio_geral.csv', [
    ['Curso', 'Categoria', 'Aulas', '% Conclusão média'],
    ...cursos.map(c => [c.titulo, c.categoria || '', flattenAulas(c).length, conclusaoPorCurso.find(x => x.titulo === c.titulo)?.pct || 0]),
  ]);
  const exportHistoricoNotas = () => downloadCsv('inep_notas.csv', [
    ['Aluno', 'Email', 'Curso', 'Nota'],
    ...todasMatriculas.filter(m => typeof m.notaAvaliacao === 'number').map(m => [m.userNome, m.userEmail, cursos.find(c => c.id === m.cursoId)?.titulo || m.cursoId, m.notaAvaliacao]),
  ]);
  const exportCertificadosCsv = () => downloadCsv('inep_certificados.csv', [
    ['Aluno', 'Email', 'Curso', 'Código', 'Emitido em'],
    ...certificados.map(c => [c.userNome, c.userEmail, c.cursoTitulo, c.codigo, fmtData(c.emitidoEm)]),
  ]);
  const exportFrequenciaCsv = () => downloadCsv('inep_frequencia.csv', [
    ['Aluno', 'Curso', 'Frequência %'],
    ...todasMatriculas.map(m => { const c = cursos.find(x => x.id === m.cursoId); return [m.userNome, c?.titulo || m.cursoId, c ? progressoCurso(c, m) : 0]; }),
  ]);

  // ── Admin: controle de acesso (libera aluno e/ou admin restrito do INEP) ────
  const [grantModal, setGrantModal] = useState(null);
  const [grantErro, setGrantErro] = useState('');
  const abrirGrant = () => { setGrantErro(''); setGrantModal({ tipo: 'aluno', nome: '', email: '', abas: [] }); };
  const editarGrant = (u) => {
    setGrantErro('');
    const restrito = inepAdmins.find(a => (a.email || '').toLowerCase() === (u.email || '').toLowerCase());
    setGrantModal({ userId: u.id, tipo: restrito ? 'admin' : 'aluno', nome: u.name || '', email: u.email || '', abas: restrito?.abas || [] });
  };
  const toggleAba = (abaId) => setGrantModal(g => ({ ...g, abas: g.abas.includes(abaId) ? g.abas.filter(x => x !== abaId) : [...g.abas, abaId] }));
  const salvarGrant = async () => {
    const alvoEmail = grantModal.email?.trim().toLowerCase();
    if (!alvoEmail) return;
    setGrantErro('');
    // Precisa ser um usuário já existente no Quality (o INEP herda o login de lá)
    const snap = await getDocs(query(collection(db, getCollectionName('users')), where('email', '==', alvoEmail)));
    if (snap.empty) {
      setGrantErro('Não achei nenhum usuário do Quality com esse e-mail. Crie o usuário no Quality primeiro (Configurações → Usuários).');
      return;
    }
    const userId = snap.docs[0].id;
    await updateDoc(doc(db, getCollectionName('users'), userId), { 'permissions.inep_ensino': { view: true, edit: false } });
    const restritoId = slugEmail(alvoEmail);
    if (grantModal.tipo === 'admin') {
      await setDoc(doc(db, getCollectionName('inep_admins'), restritoId), { nome: grantModal.nome, email: alvoEmail, abas: grantModal.abas });
    } else {
      const existiaAdmin = inepAdmins.find(a => (a.email || '').toLowerCase() === alvoEmail);
      if (existiaAdmin) await deleteDoc(doc(db, getCollectionName('inep_admins'), existiaAdmin.id));
    }
    setGrantModal(null);
  };
  const revogarAcesso = async (u) => {
    await updateDoc(doc(db, getCollectionName('users'), u.id), { 'permissions.inep_ensino': { view: false, edit: false } });
    const restrito = inepAdmins.find(a => (a.email || '').toLowerCase() === (u.email || '').toLowerCase());
    if (restrito) await deleteDoc(doc(db, getCollectionName('inep_admins'), restrito.id));
  };

  // ── Navegação ────────────────────────────────────────────────────────────────
  const NAV_ALUNO = [
    { id: 'dash', ic: '🏠', label: T('menu.dash') },
    { id: 'quemsomos', ic: '🏥', label: T('menu.quemsomos') },
    { id: 'cursos', ic: '🎒', label: T('menu.cursos') },
    { id: 'continuar', ic: '▶️', label: T('menu.continuar'), onClick: handleContinuarAula },
    { id: 'avaliacoes', ic: '📝', label: T('menu.avaliacoes') },
    { id: 'certs', ic: '🏅', label: T('menu.certs') },
    { id: 'chat', ic: '💬', label: T('menu.chat') },
    { id: 'jogo', ic: '🎮', label: T('menu.jogo') },
    { id: 'conquistas', ic: '🏆', label: T('menu.conquistas') },
    { id: 'perfil', ic: '👤', label: T('menu.perfil') },
  ];
  const abasAdminVisiveis = ADMIN_TABS.filter(t => minhasAbasAdmin.includes(t.id));
  const gruposAdmin = [...new Set(abasAdminVisiveis.map(t => t.group))];

  const mostrarNavAluno = !isAdmin || verComoAluno;

  // ── Acesso bloqueado por padrão — só entra quem foi liberado como aluno ou admin ──
  if (!meuUserLoaded) {
    return (
      <div className="inep-app" data-theme={theme}>
        <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
          <div style={{ color: 'var(--muted)' }}>Carregando…</div>
        </div>
      </div>
    );
  }
  if (!meuNivelAcesso) {
    return (
      <div className="inep-app" data-theme={theme}>
        <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 20 }}>
          <div className="inep-card" style={{ maxWidth: 420, textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: 44, marginBottom: 14 }}>🔒</div>
            <h2 style={{ margin: '0 0 8px' }}>Acesso restrito</h2>
            <p style={{ color: 'var(--muted)', fontSize: 13.5, marginBottom: 22 }}>Você ainda não tem acesso à plataforma INEP. Solicite acesso ao RH ou ao administrador do sistema.</p>
            <button className="inep-p1" style={{ background: 'var(--blue)', color: '#fff', width: 'auto', padding: '10px 20px' }} onClick={() => navigate('/home')}>↩ Voltar ao Quality</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="inep-app" data-theme={theme} style={estiloTema}>
      <div className="inep-app-wrap">
        <aside className={`inep-side ${sidePinned ? '' : 'inep-collapsed'}`}>
          <button className="inep-side-collapse-btn" onClick={() => setSidePinned(v => !v)}>{sidePinned ? '‹' : '›'}</button>
          {/* Menu aberto: logo inteira num cartão branco. Menu recolhido: só a letra. */}
          <div className="inep-brand inep-brand-com-logo">
            <div className="inep-mark">{T('marca.sigla').slice(0, 1)}</div>
            <div className="inep-brand-logo"><img src={config?.logoUrl || '/inep/logo-inep.png'} alt={`${T('marca.sigla')} — ${T('marca.nome')}`} /></div>
          </div>
          <div className="inep-rolebadge"><i /><span>{isAdmin && !verComoAluno ? 'Admin' + (isAdminMaster ? ' master' : ' restrito') + ' · online' : 'Aluno · online'}</span></div>

          <div className="inep-nav">
            {mostrarNavAluno ? (
              NAV_ALUNO.map(n => (
                <button key={n.id} className={page === n.id ? 'inep-active' : ''} onClick={n.onClick || (() => setPage(n.id))}>
                  <span className="inep-ic">{n.ic}</span><span>{n.label}</span>
                </button>
              ))
            ) : (
              gruposAdmin.map(grupo => (
                <React.Fragment key={grupo}>
                  <div className="inep-label">{grupo}</div>
                  {abasAdminVisiveis.filter(t => t.group === grupo).map(t => (
                    <button key={t.id} className={page === t.id ? 'inep-active' : ''} onClick={() => setPage(t.id)}>
                      <span className="inep-ic">{t.ic}</span><span>{t.label}</span>
                    </button>
                  ))}
                </React.Fragment>
              ))
            )}
            {isAdmin && (<>
              <hr />
              <button className={page === 'quemsomos' ? 'inep-active' : ''} onClick={() => setPage('quemsomos')}><span className="inep-ic">🏥</span><span>Quem somos</span></button>
              <button onClick={() => setVerComoAluno(v => !v)}><span className="inep-ic">👀</span><span>{verComoAluno ? 'Voltar ao admin' : 'Ver como aluno'}</span></button>
            </>)}
          </div>

          <div className="inep-side-foot">
            {!ehSoInep && <button onClick={() => navigate('/home')}><span className="inep-ic">↩</span><span>{T('menu.voltar')}</span></button>}
            <button onClick={sair}><span className="inep-ic">🚪</span><span>{T('menu.sair')}</span></button>
          </div>
        </aside>

        <main className="inep-main">
          <div className="inep-top">
            <div><h1>{T('topo.saudacao', { nome: primeiroNome })}</h1><div className="inep-sub">{T('topo.subtitulo')}</div></div>
            <div className="inep-user">
              <button className="inep-theme-toggle" onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')} title="Alternar tema">{theme === 'dark' ? '☀️' : '🌙'}</button>
              <div className="inep-u-txt"><b>{userNome}</b><small>{isAdmin && !verComoAluno ? (isAdminMaster ? 'Admin master' : 'Admin restrito') : 'Aluno'}</small></div>
              <button onClick={sair} title={T('menu.sair')} style={{ padding: '7px 12px', borderRadius: 9, border: '1px solid var(--line)', background: 'var(--card)', color: 'var(--ink)', fontSize: 12.5, fontWeight: 700 }}>🚪 {T('menu.sair')}</button>
              <div className="inep-avatar" style={meuPerfil?.avatarUrl ? { padding: 0, overflow: 'hidden' } : undefined}>
                {meuPerfil?.avatarUrl ? <img src={meuPerfil.avatarUrl} alt={userNome} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} /> : userNome.slice(0, 2).toUpperCase()}
              </div>
            </div>
          </div>

          {/* ===== DASHBOARD ===== */}
          {page === 'dash' && (
            <div className="inep-page inep-active">
              <div className={`inep-hero ${(CORES_CURSO[corDestaque] || CORES_CURSO.blue).css}`}>
                <h2>{heroTitulo}</h2>
                <p>{heroTexto}</p>
                <div className="inep-row"><button className="inep-p1" onClick={() => setPage('cursos')}>{T('dash.botaoCursos')}</button><button className="inep-p2" onClick={() => setPage('quemsomos')}>{T('dash.botaoQuemSomos')}</button></div>
              </div>
              {config?.heroVideoUrl && toYoutubeEmbed(config.heroVideoUrl) && (
                <div className="inep-video-box" style={{ marginTop: 18 }}><iframe src={toYoutubeEmbed(config.heroVideoUrl)} title="Boas-vindas" allowFullScreen /></div>
              )}
              <div className="inep-kpis">
                <KpiRing p={kpis.total ? Math.round(kpis.emAndamento / kpis.total * 100) : 0} color="var(--blue)" hole={`${kpis.emAndamento}/${kpis.total}`} label={`${kpis.emAndamento} de ${kpis.total}`} sub={T('dash.kpi1')} />
                <KpiRing p={kpis.geral} color="var(--mint)" hole={`${kpis.geral}%`} label={`${kpis.geral}%`} sub={T('dash.kpi2')} />
                <KpiRing p={87} color="var(--sun)" hole={kpis.media} label={kpis.media} sub={T('dash.kpi3')} />
                <KpiRing p={100} color="var(--coral)" hole={kpis.certificados} label={String(kpis.certificados).padStart(2, '0')} sub={T('dash.kpi4')} />
              </div>
              <div className="inep-section-title"><h3>{T('dash.continue')}</h3><span className="inep-lnk" onClick={() => setPage('cursos')}>{T('dash.verTodos')}</span></div>
              <div className="inep-grid-courses">
                {cursos.length === 0 && <div className="inep-card">{T('dash.semCursos')}{isAdmin && ' Vá em Cursos & módulos para criar o primeiro.'}</div>}
                {cursos.slice(0, 6).map(c => <CourseCard key={c.id} {...propsCard(c)} />)}
              </div>
              {config?.bannerDashboardUrl && <img src={config.bannerDashboardUrl} alt="" style={{ width: '100%', borderRadius: 18, marginTop: 22 }} />}
            </div>
          )}

          {/* ===== QUEM SOMOS ===== */}
          {page === 'quemsomos' && (
            <div className="inep-page inep-active">
              <div className="inep-qs-hero inep-card">
                <div>
                  <span className="inep-tag" style={{ background: '#e7f3fb', color: 'var(--blue-deep)' }}>{T('qs.etiqueta')}</span>
                  <h2>{T('qs.titulo')}</h2>
                  <p style={{ whiteSpace: 'pre-line' }}>{T('qs.texto')}</p>
                </div>
                <div className="inep-qs-badge">
                  <b>{T('qs.numero1')}</b><span>{T('qs.numero1Texto')}</span>
                  <hr style={{ border: 0, borderTop: '1px solid var(--line)', margin: '14px 0' }} />
                  <b>{T('qs.numero2')}</b><span>{T('qs.numero2Texto')}</span>
                </div>
              </div>

              <div className="inep-section-title"><h3>Missão, visão e valores</h3></div>
              <div className="inep-grid-courses" style={{ gridTemplateColumns: 'repeat(3,1fr)' }}>
                <div className="inep-card"><h3>Missão</h3><p className="inep-sub" style={{ marginBottom: 0, whiteSpace: 'pre-line' }}>{T('qs.missao')}</p></div>
                <div className="inep-card"><h3>Visão</h3><p className="inep-sub" style={{ marginBottom: 0, whiteSpace: 'pre-line' }}>{T('qs.visao')}</p></div>
                <div className="inep-card"><h3>Propósito</h3><p className="inep-sub" style={{ marginBottom: 0, whiteSpace: 'pre-line' }}>{T('qs.proposito')}</p></div>
              </div>

              <div className="inep-vals">
                {qsValores.map((v, i) => (
                  <div key={i} className="inep-val-card"><div className="inep-dot" style={{ background: v.cor }}>{(v.titulo || '?')[0]}</div><b>{v.titulo}</b><p>{v.texto}</p></div>
                ))}
              </div>

              <div className="inep-section-title"><h3>Nossa trajetória</h3></div>
              <div className="inep-card"><div className="inep-timeline inep-scrollx">
                {qsTimeline.map((t, i) => (
                  <div key={i} className="inep-tl-item"><div className="inep-tl-dot" /><b>{t.ano}</b><span>{t.texto}</span></div>
                ))}
              </div></div>

              <div className="inep-section-title"><h3>Nossas unidades</h3></div>
              <div className="inep-units-grid">
                {qsUnidades.map((u, i) => (
                  <div key={i} className="inep-unit-card">
                    <div className="inep-unit-banner" style={{ background: `linear-gradient(135deg,${u.cor1 || '#075f94'},${u.cor2 || '#22b8d9'})` }}>
                      <span className="inep-pill">{u.etiqueta}</span>
                      <div className="inep-wm">{(u.nome || '?')[0]}</div>
                      <div className="inep-word">{u.nome}</div>
                    </div>
                    <div className="inep-unit-info"><span>{u.texto}</span></div>
                  </div>
                ))}
              </div>

              <div className="inep-quote-box"><div className="inep-q-mark">"</div><div><p>{T('qs.citacao')}</p><small>{T('qs.citacaoAutor')}</small></div></div>
            </div>
          )}

          {/* ===== CURSOS ===== */}
          {page === 'cursos' && (
            <div className="inep-page inep-active">
              <div className="inep-section-title" style={{ marginTop: 0 }}><h3>{T('cursos.titulo')}</h3>
                <input value={buscaCatalogo} onChange={e => setBuscaCatalogo(e.target.value)} placeholder="🔍 Buscar curso" style={{ padding: '8px 12px', border: '1.5px solid var(--line)', borderRadius: 10, background: 'var(--card)', color: 'var(--ink)', minWidth: 200 }} />
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                {[['todos', 'Todos'], ['favoritos', '♥ Favoritos'], ['andamento', 'Em andamento'], ['concluidos', 'Concluídos']].map(([v, l]) => (
                  <button key={v} onClick={() => setFiltroCatalogo(v)} style={{ padding: '7px 14px', borderRadius: 20, fontWeight: 700, fontSize: 12.5, background: filtroCatalogo === v ? 'var(--blue)' : 'var(--card)', color: filtroCatalogo === v ? '#fff' : 'var(--ink)', border: '1px solid var(--line)' }}>{l}</button>
                ))}
              </div>
              {(() => {
                const lista = cursos.filter(c => {
                  const pct = progressoCurso(c, minhasMatriculas[c.id]);
                  if (filtroCatalogo === 'favoritos' && !favoritos.has(c.id)) return false;
                  if (filtroCatalogo === 'andamento' && !(pct > 0 && pct < 100)) return false;
                  if (filtroCatalogo === 'concluidos' && pct < 100) return false;
                  const q = buscaCatalogo.trim().toLowerCase();
                  return !q || `${c.titulo} ${c.categoria || ''} ${c.descricao || ''}`.toLowerCase().includes(q);
                });
                return (
                  <div className="inep-grid-courses">
                    {lista.length === 0 && <div className="inep-card">{cursos.length ? 'Nenhum curso neste filtro.' : 'Nenhum curso disponível para você ainda.'}</div>}
                    {lista.map(c => <CourseCard key={c.id} {...propsCard(c)} />)}
                  </div>
                );
              })()}
            </div>
          )}

          {/* ===== CONTINUAR (vazio) ===== */}
          {page === 'continuar_vazio' && (
            <div className="inep-page inep-active">
              <div className="inep-card" style={{ textAlign: 'center', padding: 50 }}>
                <div style={{ fontSize: 40, marginBottom: 10 }}>🎉</div>
                <b>{T('continuar.vazio')}</b>
                <p className="inep-sub" style={{ marginTop: 8 }}>Assim que um novo curso for publicado, ele aparece aqui.</p>
              </div>
            </div>
          )}

          {/* ===== AULA / PLAYER ===== */}
          {page === 'lesson' && lessonCurso && (
            <div className="inep-page inep-active">
              <div className="inep-lesson-head">
                <div><h3 style={{ margin: '0 0 4px' }}>{lessonCurso.titulo}</h3><span className="inep-tag" style={{ background: '#e7f3fb', color: 'var(--blue-deep)' }}>{lessonAula?.moduloTitulo || 'Módulo'}</span></div>
                <div style={{ color: 'var(--muted)', fontSize: 13 }}>{progressoCurso(lessonCurso, lessonMat)}% concluído</div>
              </div>
              <div className="inep-lesson-grid">
                <div>
                  <div className="inep-video-box">
                    {ehVideoProprio(lessonAula?.videoArquivo) ? (
                      <VideoProprio refVideo={lessonAula.videoArquivo} titulo={lessonAula.titulo}
                        onTerminou={() => { if (!lessonFeitas.has(lessonAula.id)) marcarAulaConcluida(); }} />
                    ) : lessonAula?.videoUrl && toYoutubeEmbed(lessonAula.videoUrl) ? (
                      <iframe src={toYoutubeEmbed(lessonAula.videoUrl)} title={lessonAula.titulo} allowFullScreen />
                    ) : (
                      <div className="inep-video-empty"><div className="inep-play">▶</div><b>{T('aula.semVideo')}</b><small>O administrador pode enviar o vídeo ou colar o link do YouTube em Cursos & módulos.</small></div>
                    )}
                  </div>
                  <h2 style={{ margin: '16px 0 6px' }}>{lessonAula?.titulo}</h2>
                  <p style={{ color: 'var(--muted)', fontSize: 13.5 }}>{T('aula.texto')}</p>
                  <div className="inep-card">
                    <b>{T('aula.atividade')}</b>
                    <p style={{ color: 'var(--muted)', fontSize: 13 }}>{lessonFeitas.has(lessonAula?.id) ? 'Você já concluiu esta aula.' : T('aula.texto')}</p>
                    <button className="inep-p1" style={{ background: 'var(--blue)', color: '#fff', width: 'auto', padding: '10px 18px' }} onClick={marcarAulaConcluida} disabled={lessonFeitas.has(lessonAula?.id)}>
                      {lessonFeitas.has(lessonAula?.id) ? T('aula.concluida') : T('aula.botao')}
                    </button>
                  </div>
                </div>
                <div className="inep-playlist">
                  <h4>📑 Conteúdo do curso</h4>
                  {(lessonCurso.modulos || []).map(mod => (
                    <div key={mod.id} className="inep-mod-group">
                      <div className="inep-mod-header">{mod.titulo} <small>{(mod.aulas || []).filter(a => lessonFeitas.has(a.id)).length}/{(mod.aulas || []).length}</small></div>
                      <div className="inep-mod-body inep-open">
                        {(mod.aulas || []).map(a => (
                          <div key={a.id} className={`inep-lessonitem ${a.id === lessonAulaId ? 'inep-current' : ''} ${lessonFeitas.has(a.id) ? 'inep-done' : ''}`} onClick={() => setLessonAulaId(a.id)}>
                            <b>{lessonFeitas.has(a.id) ? '✓ ' : ''}{a.titulo}</b>
                            <span className="inep-dur">{a.duracaoMin ? `${a.duracaoMin}min` : ''}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {lessonCurso.avaliacao?.perguntas?.length > 0 && (
                    <div className={`inep-lessonitem ${typeof lessonMat?.notaAvaliacao === 'number' ? 'inep-done' : ''}`} onClick={() => { setExamCursoId(lessonCurso.id); setPage('avaliacoes'); }}>
                      <b>{typeof lessonMat?.notaAvaliacao === 'number' ? '✓ ' : '○ '}Avaliação do curso</b>
                      <span className="inep-dur">—</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ===== AVALIAÇÕES ===== */}
          {page === 'avaliacoes' && (
            <div className="inep-page inep-active">
              <div className="inep-section-title" style={{ marginTop: 0 }}><h3>{T('aval.titulo')}</h3></div>
              {!examCurso && (
                <div className="inep-grid-courses" style={{ gridTemplateColumns: 'repeat(2,1fr)' }}>
                  {cursosComExame.length === 0 && <div className="inep-card">Nenhum curso com avaliação cadastrada ainda.</div>}
                  {cursosComExame.map(c => {
                    const mat = minhasMatriculas[c.id];
                    const pct = progressoCurso(c, mat);
                    const feita = typeof mat?.notaAvaliacao === 'number';
                    return (
                      <div key={c.id} className="inep-card">
                        <h3>{c.titulo}</h3>
                        <p className="inep-sub">{feita ? `Nota: ${mat.notaAvaliacao}% ${mat.notaAvaliacao >= minimoAprovacao(c) ? '✅' : '— tente novamente'}` : pct < 100 ? `Assista todas as aulas primeiro (${pct}% concluído)` : `${c.avaliacao.perguntas.length} pergunta(s)`}</p>
                        <button disabled={pct < 100} style={{ background: pct < 100 ? '#94a3b8' : 'var(--blue)', color: '#fff', padding: '9px 16px', borderRadius: 10, fontWeight: 700 }}
                          onClick={() => { setExamCursoId(c.id); setExamRespostas({}); setExamResultado(null); }}>
                          {feita ? 'Refazer avaliação' : 'Fazer avaliação'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              {examCurso && (
                <div className="inep-card">
                  <h3>{examCurso.titulo} — Avaliação</h3>
                  {examResultado ? (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                      <div style={{ fontSize: 40, marginBottom: 10 }}>{examResultado.passou ? '🎉' : '📖'}</div>
                      <b style={{ fontSize: 18 }}>Nota: {examResultado.nota}%</b>
                      <p style={{ color: 'var(--muted)' }}>{examResultado.passou ? T('aval.aprovado') : T('aval.reprovado', { minimo: `${minimoAprovacao(examCurso)}%` })}</p>
                      <button className="inep-p1" style={{ background: 'var(--blue)', color: '#fff', width: 'auto', padding: '10px 18px' }} onClick={() => { setExamCursoId(null); setExamResultado(null); }}>Voltar às avaliações</button>
                    </div>
                  ) : (
                    <>
                      {examCurso.avaliacao.perguntas.map((p, i) => (
                        <div key={i} className="inep-exam-q">
                          <b>{i + 1}. {p.pergunta}</b>
                          {(p.opcoes || []).map((op, oi) => (
                            <label key={oi} className={`inep-exam-opt ${examRespostas[i] === oi ? 'inep-sel' : ''}`}>
                              <input type="radio" name={`q${i}`} checked={examRespostas[i] === oi} onChange={() => setExamRespostas(r => ({ ...r, [i]: oi }))} />
                              {op}
                            </label>
                          ))}
                        </div>
                      ))}
                      <button className="inep-p1" style={{ background: 'var(--blue)', color: '#fff', width: 'auto', padding: '10px 18px' }}
                        disabled={enviandoProva || Object.keys(examRespostas).length < examCurso.avaliacao.perguntas.length}
                        onClick={submeterAvaliacao}>
                        {enviandoProva ? 'Corrigindo…' : 'Enviar avaliação'}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ===== CERTIFICADOS ===== */}
          {page === 'certs' && (
            <div className="inep-page inep-active">
              <div className="inep-section-title" style={{ marginTop: 0 }}><h3>{T('cert.titulo')}</h3><span style={{ color: 'var(--muted)', fontSize: 12.5 }}>Liberado automaticamente ao concluir 100% de um curso</span></div>
              <div className="inep-grid-courses">
                {meusCertificados.length === 0 && <div className="inep-card">Você ainda não tem certificados. Conclua um curso pra receber o primeiro!</div>}
                {meusCertificados.map(cert => {
                  const virado = !!certFlipped[cert.id];
                  return (
                    <div key={cert.id} className="inep-card">
                      <CertificadoPrevia lado={virado ? 'verso' : 'frente'}
                        d={{ ...montarOficial(cert, meuPerfil?.nomeCompleto || cert.userNome || userNome, meuPerfil?.documento || ''), codigo: cert.codigo }} />
                      <button className="inep-p1" style={{ marginTop: 10, width: '100%', background: 'var(--blue)', color: '#fff', padding: '10px', borderRadius: 10, fontWeight: 700 }}
                        disabled={gerandoCert === cert.id} onClick={() => baixarCertificado(cert)}>
                        {gerandoCert === cert.id ? 'Gerando…' : '📄 Baixar certificado (PDF)'}
                      </button>
                      <button className="inep-add-aula" style={{ marginTop: 6, width: '100%' }} onClick={() => setCertFlipped(f => ({ ...f, [cert.id]: !f[cert.id] }))}>
                        {virado ? '↺ Ver frente' : '↻ Ver verso'}
                      </button>
                      <div className="inep-avaliar" style={{ marginTop: 10, justifyContent: 'center' }}>
                        {notaCurso[cert.cursoId]?.minha ? 'Sua avaliação do curso:' : 'Avalie este curso:'}
                        <Estrelas valor={notaCurso[cert.cursoId]?.minha || 0} tamanho={18} onEscolher={n => avaliarCurso(cert.cursoId, n)} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ===== CHAT COM INSTRUTORES ===== */}
          {page === 'chat' && (
            <div className="inep-page inep-active">
              <div className="inep-section-title" style={{ marginTop: 0 }}><h3>{T('chat.titulo')}</h3></div>
              <ChatPanel />
            </div>
          )}

          {/* ===== JOGO EDUCATIVO ===== */}
          {page === 'jogo' && (
            <div className="inep-page inep-active">
              <div className="inep-top" style={{ marginBottom: 14 }}>
                <div><h3 style={{ margin: 0 }}>{T('jogo.titulo')}</h3><div className="inep-sub">{T('jogo.subtitulo')}</div></div>
              </div>
              <div className="inep-game-wrap">
                <div className="inep-game-field" tabIndex={0} onKeyDown={e => { if (e.key === 'ArrowLeft') moverJogador(-1); if (e.key === 'ArrowRight') moverJogador(1); }}>
                  <div className="inep-game-hud"><span>Pontos: {score}</span><span>{'❤️'.repeat(Math.max(0, lives))}</span></div>
                  <div className="inep-game-player" style={{ left: `${(playerLane + 0.5) * 33.333}%` }}>🧑‍⚕️</div>
                  {hazards.map(h => <div key={h.id} className="inep-game-hazard" style={{ left: `${(h.lane + 0.5) * 33.333}%`, top: `${h.y}%` }}>📦</div>)}
                  {(!gameRunning) && (
                    <div className="inep-game-overlay">
                      {gameOver ? (
                        <div><b style={{ fontSize: 20 }}>Fim de jogo!</b><p>Você sobreviveu com {score} pontos.</p><button onClick={startGame}>Jogar novamente</button></div>
                      ) : (
                        <div><b style={{ fontSize: 20 }}>Segurança em Ação</b><p>Desvie dos riscos e proteja o profissional</p><button onClick={startGame}>▶ Começar</button></div>
                      )}
                    </div>
                  )}
                  {gameRunning && (
                    <div style={{ position: 'absolute', bottom: 8, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 40 }}>
                      <button onClick={() => moverJogador(-1)} style={{ background: 'var(--card)', borderRadius: '50%', width: 40, height: 40, boxShadow: 'var(--shadow)' }}>◀</button>
                      <button onClick={() => moverJogador(1)} style={{ background: 'var(--card)', borderRadius: '50%', width: 40, height: 40, boxShadow: 'var(--shadow)' }}>▶</button>
                    </div>
                  )}
                </div>
                <div className="inep-card inep-game-info">
                  <b style={{ fontSize: 15, display: 'block', marginBottom: 10 }}>Como jogar</b>
                  <p className="inep-sub">Um minijogo educativo sobre segurança do trabalho</p>
                  <div className="inep-field"><b>Objetivo</b><span>Sobreviver o máximo</span></div>
                  <div className="inep-field"><b>Controles</b><span>← → ou toque</span></div>
                  <div className="inep-field"><b>Vidas</b><span>3 tentativas</span></div>
                  <div className="inep-notice">{T('jogo.dica')}</div>
                </div>
              </div>
            </div>
          )}

          {/* ===== CONQUISTAS (emblemas + ranking) ===== */}
          {page === 'conquistas' && (
            <div className="inep-page inep-active">
              <div className="inep-section-title" style={{ marginTop: 0 }}><h3>{T('conq.titulo')}</h3><span style={{ color: 'var(--muted)', fontSize: 12.5 }}>{meusEmblemas.filter(b => b.unlocked).length} de {meusEmblemas.length} desbloqueados</span></div>
              <div className="inep-badge-grid">
                {meusEmblemas.map(b => (
                  <div key={b.id} className={`inep-badge-card ${b.unlocked ? '' : 'inep-locked'}`}>
                    <div className="inep-badge-ic">{b.ic}</div>
                    <b>{b.nome}</b>
                    <p>{b.desc}</p>
                  </div>
                ))}
              </div>

              <div className="inep-section-title"><h3>{T('conq.ranking')}</h3><span style={{ color: 'var(--muted)', fontSize: 12.5 }}>Por progresso médio entre os cursos</span></div>
              <div className="inep-card">
                {alunosAgg.length === 0 && <div style={{ color: 'var(--muted)', textAlign: 'center', padding: 20 }}>Ninguém iniciou um curso ainda.</div>}
                {alunosAgg.map((a, i) => (
                  <div key={a.email} className={`inep-rank-row ${a.email === email ? 'inep-me' : ''}`}>
                    <b>{i + 1}º</b>
                    <span className="inep-mini-avatar">{(a.nome || '?').slice(0, 2).toUpperCase()}</span>
                    <span style={{ flex: 1 }}>{a.nome}{a.email === email ? ' (você)' : ''}</span>
                    <span>{a.progressoMedio}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ===== MEU PERFIL ===== */}
          {page === 'perfil' && (
            <div className="inep-page inep-active">
              <div className="inep-section-title" style={{ marginTop: 0 }}><h3>Meu perfil</h3><span style={{ color: 'var(--muted)', fontSize: 12.5 }}>Visível só para você e para os administradores do INEP</span></div>
              <div className="inep-dtabs" style={{ marginBottom: 16 }}>
                {[['dados', 'Meus dados'], ['progresso', 'Progresso'], ['certificados', 'Certificados'], ['conquistas', 'Conquistas']].map(([id, label]) => (
                  <button key={id} className={perfilTab === id ? 'inep-sel' : ''} onClick={() => setPerfilTab(id)}>{label}</button>
                ))}
              </div>
              {perfilTab === 'dados' && (
                <InepPerfil email={email} nome={userNome} userDoc={userDoc} perfil={meuPerfil} carregado={perfilCarregado}
                  turmasNomes={turmas.filter(t => minhasTurmas.has(t.id)).map(t => t.nome)} />
              )}
              <div className="inep-card" style={{ maxWidth: 720, display: perfilTab === 'dados' ? 'none' : undefined }}>
                {perfilTab === 'progresso' && (
                  <div className="inep-fields">
                    {cursos.length === 0 && <div style={{ color: 'var(--muted)' }}>Nenhum curso publicado ainda.</div>}
                    {cursos.map(c => (
                      <div key={c.id} className="inep-field"><b>{c.titulo}</b><span>{progressoCurso(c, minhasMatriculas[c.id])}%</span></div>
                    ))}
                  </div>
                )}
                {perfilTab === 'certificados' && (
                  <div className="inep-fields">
                    {meusCertificados.length === 0 && <div style={{ color: 'var(--muted)' }}>Nenhum certificado ainda.</div>}
                    {meusCertificados.map(c => (
                      <div key={c.id} className="inep-field"><b>{c.cursoTitulo}</b><span>{fmtData(c.emitidoEm)}</span></div>
                    ))}
                  </div>
                )}
                {perfilTab === 'conquistas' && (
                  <div className="inep-badge-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
                    {meusEmblemas.map(b => (
                      <div key={b.id} className={`inep-badge-card ${b.unlocked ? '' : 'inep-locked'}`}>
                        <div className="inep-badge-ic">{b.ic}</div><b>{b.nome}</b><p>{b.desc}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ===== ADMIN: PAINEL GERAL ===== */}
          {page === 'painel' && isAdmin && (
            <div className="inep-page inep-active">
              <div className="inep-hero inep-hero-mint">
                <h2>Visão completa da plataforma, em tempo real.</h2>
                <p>Como administrador, você acompanha alunos, cursos, notas, frequência e certificados desde o primeiro acesso — e decide quais abas cada admin da sua equipe pode ver.</p>
                <div className="inep-row"><button className="inep-p1" onClick={() => setPage('alunos')}>Ver alunos</button><button className="inep-p2" onClick={() => setPage('relatorios')}>Abrir relatórios</button></div>
              </div>
              <div className="inep-kpis">
                <KpiRing p={100} color="var(--blue)" hole={alunosAgg.length} label={String(alunosAgg.length)} sub="Alunos ativos" />
                <KpiRing p={100} color="var(--coral)" hole={cursos.length} label={String(cursos.length)} sub="Cursos publicados" />
                <KpiRing p={alunosAgg.length ? Math.round(alunosAgg.reduce((a, b) => a + b.progressoMedio, 0) / alunosAgg.length) : 0} color="var(--mint)" hole={`${alunosAgg.length ? Math.round(alunosAgg.reduce((a, b) => a + b.progressoMedio, 0) / alunosAgg.length) : 0}%`} label={`${alunosAgg.length ? Math.round(alunosAgg.reduce((a, b) => a + b.progressoMedio, 0) / alunosAgg.length) : 0}%`} sub="Conclusão média" />
                <KpiRing p={100} color="var(--sun)" hole={certificados.length} label={String(certificados.length)} sub="Certificados emitidos" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 18, marginTop: 20 }}>
                <div className="inep-card">
                  <h3>Atividade recente</h3><p className="inep-sub">Últimos acessos e progresso dos alunos</p>
                  <table className="inep-table">
                    <thead><tr><th>Aluno</th><th>Curso</th><th>Progresso</th><th>Status</th></tr></thead>
                    <tbody>
                      {[...todasMatriculas].sort((a, b) => tsVal(b.atualizadoEm) - tsVal(a.atualizadoEm)).slice(0, 6).map(m => {
                        const curso = cursos.find(c => c.id === m.cursoId);
                        return (
                          <tr key={m.id}>
                            <td><span className="inep-mini-avatar">{(m.userNome || '?').slice(0, 2).toUpperCase()}</span>{m.userNome}</td>
                            <td>{curso?.titulo || m.cursoId}</td>
                            <td>{curso ? progressoCurso(curso, m) : 0}%</td>
                            <td><span className="inep-tag" style={{ background: m.concluidoEm ? '#dcfce7' : '#fef9c3', color: m.concluidoEm ? '#15803d' : '#a16207' }}>{m.concluidoEm ? 'Concluído' : 'Em andamento'}</span></td>
                          </tr>
                        );
                      })}
                      {todasMatriculas.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--muted)' }}>Nenhuma atividade ainda.</td></tr>}
                    </tbody>
                  </table>
                </div>
                <div className="inep-card">
                  <h3>Ações rápidas</h3><p className="inep-sub">Acesso direto às funções administrativas</p>
                  <button className="inep-add-aula" style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 8 }} onClick={() => { abrirNovoCurso(); setPage('cursos_admin'); }}>+ Criar novo curso</button>
                  <button className="inep-add-aula" style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 8, background: '#fdeceb', color: 'var(--coral-deep)' }} onClick={() => setEscolherCursoAvaliacao(true)}>📝 Lançar avaliação</button>
                  <button className="inep-add-aula" style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 8, background: '#dcfce7', color: '#15803d' }} onClick={() => setPage('certificados_admin')}>🏅 Gerenciar certificados</button>
                  <button className="inep-add-aula" style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 8 }} onClick={() => setPage('relatorios')}>📋 Exportar relatório geral</button>
                  <button className="inep-add-aula" style={{ display: 'block', width: '100%', textAlign: 'left' }} onClick={() => setPage('personalizacao')}>🎨 Personalizar tela inicial</button>
                </div>
              </div>
            </div>
          )}

          {/* ===== ADMIN: ALUNOS ===== */}
          {page === 'alunos' && isAdmin && (
            <div className="inep-page inep-active">
              {(() => {
                const norm = s => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
                const aggPorEmail = Object.fromEntries(alunosAgg.map(a => [(a.email || '').toLowerCase(), a]));
                const turmasDe = e => membros.filter(m => m.email === e).map(m => turmas.find(t => t.id === m.turmaId)?.nome).filter(Boolean);
                const todos = usuariosComAcesso.map(u => ({ u, e: (u.email || '').toLowerCase() }));
                // Quem tem matrícula mas não está na lista (ex.: perdeu o acesso) também aparece
                alunosAgg.forEach(a => { if (!todos.some(x => x.e === (a.email || '').toLowerCase())) todos.push({ u: { name: a.nome, email: a.email }, e: (a.email || '').toLowerCase() }); });
                const lista = todos
                  .filter(({ u, e }) => !buscaAlunos.trim() || norm(`${u.name} ${e} ${(u.tipos || []).join(' ')} ${(u.cooperativas || []).join(' ')}`).includes(norm(buscaAlunos)))
                  .sort((a, b) => (aggPorEmail[b.e]?.progressoMedio || 0) - (aggPorEmail[a.e]?.progressoMedio || 0) || (a.u.name || '').localeCompare(b.u.name || ''));
                return (<>
                  <div className="inep-section-title" style={{ marginTop: 0 }}>
                    <h3>Alunos <small style={{ color: 'var(--muted)', fontSize: 13 }}>{todos.length} com acesso · {alunosAgg.length} já começaram</small></h3>
                    <input value={buscaAlunos} onChange={e => setBuscaAlunos(e.target.value)} placeholder="🔍 Nome, e-mail, tipo ou cooperativa" style={{ padding: '8px 12px', border: '1.5px solid var(--line)', borderRadius: 10, background: 'var(--card)', color: 'var(--ink)', minWidth: 260 }} />
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="inep-table">
                      <thead><tr><th>Aluno</th><th>Tipo</th><th>Turmas</th><th>Cursos</th><th>Progresso médio</th><th>Média</th><th>Certificados</th></tr></thead>
                      <tbody>
                        {lista.slice(0, 300).map(({ u, e }) => {
                          const a = aggPorEmail[e];
                          return (
                            <tr key={e} style={{ cursor: 'pointer' }} onClick={() => abrirPerfilAluno(e, u.name)} title="Abrir perfil">
                              <td><span className="inep-mini-avatar">{(u.name || '?').slice(0, 2).toUpperCase()}</span>{u.name || e}<br /><small style={{ color: 'var(--muted)' }}>{e}</small></td>
                              <td>{(u.tipos || []).join(', ') || 'Funcionário'}</td>
                              <td>{turmasDe(e).join(', ') || '—'}</td>
                              <td>{a?.cursosNomes?.join(', ') || '—'}</td>
                              <td>{a ? `${a.progressoMedio}%` : '—'}</td>
                              <td>{a?.mediaGeral ?? '—'}</td>
                              <td>{a?.certificadosCount || 0}</td>
                            </tr>
                          );
                        })}
                        {lista.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--muted)' }}>Ninguém encontrado.</td></tr>}
                      </tbody>
                    </table>
                    {lista.length > 300 && <div style={{ color: 'var(--muted)', fontSize: 12, padding: 8 }}>Mostrando 300 de {lista.length} — use a busca.</div>}
                  </div>
                </>);
              })()}
            </div>
          )}

          {/* ===== ADMIN: CURSOS & MÓDULOS ===== */}
          {page === 'cursos_admin' && isAdmin && (
            <div className="inep-page inep-active">
              <div className="inep-section-title" style={{ marginTop: 0 }}>
                <h3>Cursos & módulos</h3>
                <div style={{ display: 'flex', gap: 8 }}>
                  {cursosNaLixeira.length > 0 && (
                    <button className="inep-p1" style={{ background: verLixeira ? 'var(--ink)' : 'var(--bg)', color: verLixeira ? '#fff' : 'var(--ink)', border: '1px solid var(--line)', width: 'auto', padding: '10px 14px' }} onClick={() => setVerLixeira(v => !v)}>🗑 Lixeira ({cursosNaLixeira.length})</button>
                  )}
                  <button className="inep-p1" style={{ background: 'var(--blue)', color: '#fff', width: 'auto', padding: '10px 18px' }} onClick={abrirNovoCurso}>+ Novo curso</button>
                </div>
              </div>
              <p className="inep-sub">Gerencie cursos, módulos e vídeos do YouTube.</p>
              {verLixeira && cursosNaLixeira.length > 0 && (
                <div className="inep-card" style={{ marginBottom: 16 }}>
                  <h3 style={{ marginTop: 0 }}>🗑 Lixeira</h3>
                  <p className="inep-sub" style={{ marginTop: 0 }}>Cursos excluídos não aparecem para ninguém. Notas e certificados já emitidos continuam guardados e válidos.</p>
                  <table className="inep-table">
                    <thead><tr><th>Curso</th><th>Excluído em</th><th>Por</th><th /></tr></thead>
                    <tbody>
                      {cursosNaLixeira.map(c => (
                        <tr key={c.id}>
                          <td><b>{c.titulo}</b></td>
                          <td>{c.excluidoEm ? new Date(c.excluidoEm).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</td>
                          <td>{c.excluidoPor || '—'}</td>
                          <td style={{ textAlign: 'right' }}><button className="inep-p1" style={{ background: 'var(--blue)', color: '#fff', width: 'auto', padding: '6px 12px', fontSize: 12.5 }} onClick={() => restaurarCurso(c)}>↩ Restaurar</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="inep-grid-courses">
                {todosCursos.length === 0 && <div className="inep-card">Nenhum curso ainda — clique em "+ Novo curso".</div>}
                {todosCursos.map(c => {
                  const cor = CORES_CURSO[c.cor] || CORES_CURSO.blue;
                  const pct = conclusaoPorCurso.find(x => x.titulo === c.titulo)?.pct || 0;
                  const totalMin = flattenAulas(c).reduce((a, x) => a + (Number(x.duracaoMin) || 0), 0);
                  const nomesTurmas = (c.turmaIds || []).map(id => turmas.find(t => t.id === id)?.nome).filter(Boolean);
                  return (
                    <div key={c.id} className="inep-ccard inep-ccard-v2" style={c.ativo === false ? { opacity: 0.6 } : undefined}>
                      {/* mesma capa do cartão do aluno (CourseCard): com imagem, o título já vem desenhado nela */}
                      <div className={`inep-ccover ${c.capaUrl ? '' : cor.css}`} style={c.capaUrl ? { backgroundImage: `url(${c.capaUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}>
                        {c.capaUrl
                          ? c.ativo === false && <span className="inep-chip" style={{ background: 'rgba(15,23,42,.6)' }}>Inativo</span>
                          : <><span className="inep-chip">{c.categoria || 'Curso'}</span><b>{c.titulo}{c.ativo === false && ' (inativo)'}</b></>}
                      </div>
                      <div className="inep-cbody">
                        {c.capaUrl && <div className="inep-ctitle">{c.titulo}{c.ativo === false && ' (inativo)'}</div>}
                        <div style={{ fontSize: 12, marginBottom: 6, color: c.visibilidade === 'turmas' && !nomesTurmas.length ? 'var(--coral-deep)' : 'var(--muted)' }}>
                          {c.visibilidade !== 'turmas' ? '👥 Todos os alunos' : nomesTurmas.length ? `🧑‍🤝‍🧑 ${nomesTurmas.join(', ')}` : '⚠ Nenhuma turma — nenhum aluno vê'}
                        </div>
                        <div style={{ fontSize: 12, marginBottom: 6, color: 'var(--muted)' }}>
                          👔 {c.gerentes?.length ? `Gerente da área: ${c.gerentes.map(g => g.nome || g.email).join(', ')}` : 'Sem gerente da área'}
                        </div>
                        {!(c.avaliacao?.perguntas?.length > 0) && (
                          <div style={{ fontSize: 12, marginBottom: 6, color: 'var(--coral-deep)', fontWeight: 700 }}>⚠ Sem avaliação — não aparece para os alunos</div>
                        )}
                        <div className="inep-meta"><span>{flattenAulas(c).length} aulas · {totalMin ? `${Math.round(totalMin / 60 * 10) / 10}h` : '—'}</span><span>{pct}% concl. médio</span></div>
                        <div className="inep-pbar"><i style={{ width: `${pct}%`, background: cor.hex }} /></div>
                        <button style={{ background: cor.hex, color: '#fff' }} onClick={() => abrirEditorCurso(c)}>Editar curso</button>
                        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                          <button style={{ flex: 1, background: '#f1f5f9', color: '#334155' }} onClick={() => desativarCurso(c)}>{c.ativo === false ? '↩ Reativar' : '🚫 Desativar'}</button>
                          <button style={{ flex: 1, background: '#fef2f2', color: '#b91c1c' }} onClick={() => excluirCurso(c)}>🗑 Excluir</button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ===== ADMIN: ACOMPANHAMENTO DOS COLABORADORES NOVOS ===== */}
          {page === 'acompanhamento' && isAdmin && (() => {
            const N = acomp?.config?.diasAntes || 7;
            const lista = (acomp?.situacao || []).filter(p => !acompSoPendentes || p.pendentes > 0);
            const corTag = { concluido: ['#dcfce7', '#15803d'], andamento: ['#fef9c3', '#a16207'], reprovado: ['#fee2e2', '#b91c1c'], nao_iniciado: ['#f1f5f9', '#475569'] };
            const nota = n => (n / 10).toFixed(1).replace('.', ',');
            const textoCurso = c => (c.status === 'concluido' ? `✅ ${c.titulo}${c.nota !== null ? ` · nota ${nota(c.nota)}` : ''}`
              : c.status === 'reprovado' ? `❌ ${c.titulo} · nota ${nota(c.nota)} (refazer)`
              : c.status === 'andamento' ? `⏳ ${c.titulo} · ${c.feitas}/${c.total} aulas`
              : `⛔ ${c.titulo} · não começou`);
            const situacaoGeral = p => (p.pendentes === 0 ? ['✅ Em dia', '#15803d']
              : p.dias < 0 ? [`⚠️ Trabalhando há ${-p.dias} dia(s) com pendência`, '#b91c1c']
              : p.dias === 0 ? ['📅 Começa hoje com pendência', '#c2410c']
              : p.dias <= N ? [`⏳ Começa em ${p.dias} dia(s)`, '#a16207']
              : [`Começa em ${p.dias} dias`, 'var(--muted)']);
            const ult = acomp?.estado?.ultimoResultado;
            const botao = { width: 'auto', padding: '9px 14px', fontSize: 13, opacity: acompOcupado ? 0.6 : 1 };
            return (
              <div className="inep-page inep-active">
                <div className="inep-section-title" style={{ marginTop: 0 }}>
                  <h3>Acompanhamento dos colaboradores novos</h3>
                  <button className="inep-p1" style={{ ...botao, background: 'var(--bg)', color: 'var(--ink)', border: '1px solid var(--line)' }} onClick={lerAcompanhamento}>↻ Atualizar</button>
                </div>
                <p className="inep-sub">Aparece quem tem <b>Início no trabalho</b> preenchido no cadastro (Configurações → Usuários do Quality) e está numa turma com cursos. Os cursos obrigatórios são os cursos das turmas da pessoa.</p>
                {acompErro && <div className="inep-notice" style={{ marginBottom: 12 }}>⚠ {acompErro}</div>}
                {!acomp && !acompErro && <div className="inep-card">Carregando…</div>}

                {acomp && acompCfg && (
                  <div className="inep-card" style={{ marginBottom: 16 }}>
                    <h3 style={{ marginTop: 0 }}>📧 Resumo por e-mail</h3>
                    <div className="inep-form-grid">
                      <Field label="E-mails do RH (separe por vírgula)">
                        <textarea rows={2} value={acompCfg.rhEmails} onChange={e => setAcompCfg(c => ({ ...c, rhEmails: e.target.value }))} placeholder="rh@empresa.com.br, outra.pessoa@empresa.com.br" />
                      </Field>
                      <Field label="Avisar o RH com quantos dias de antecedência do início">
                        <input type="number" min="1" max="60" value={acompCfg.diasAntes} onChange={e => setAcompCfg(c => ({ ...c, diasAntes: e.target.value }))} />
                      </Field>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 13.5, cursor: 'pointer' }}>
                      <input type="checkbox" checked={acompCfg.ligado} onChange={e => setAcompCfg(c => ({ ...c, ligado: e.target.checked }))} />
                      Enviar automaticamente nos dias úteis, às 7h
                    </label>
                    <p style={{ fontSize: 12.5, color: 'var(--muted)', margin: '8px 0 12px', lineHeight: 1.5 }}>
                      O RH recebe todos os colaboradores acompanhados: quem já começou a trabalhar com pendência, quem começa hoje e quem começa nos próximos {acompCfg.diasAntes || N} dias sem ter concluído, além de quem concluiu (com a nota).
                      O <b>gerente da área</b> de cada curso (campo no cadastro do curso) recebe só os cursos dele. Só sai e-mail quando há o que avisar. Remetente: nao-responda@gestaoquality.com.br.
                    </p>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <button className="inep-p1" disabled={!!acompOcupado || !acompAlterado} style={{ ...botao, background: 'var(--blue)', color: '#fff', opacity: acompOcupado || !acompAlterado ? 0.5 : 1 }} onClick={salvarCfgAcomp}>Salvar</button>
                      <button className="inep-p1" disabled={!!acompOcupado} style={{ ...botao, background: 'var(--bg)', color: 'var(--ink)', border: '1px solid var(--line)' }} onClick={verPreviaAcomp}>👁 Ver prévia dos e-mails</button>
                      <button className="inep-p1" disabled={!!acompOcupado} style={{ ...botao, background: 'var(--bg)', color: 'var(--ink)', border: '1px solid var(--line)' }} onClick={enviarAgoraAcomp}>📨 Enviar agora</button>
                      {acompOcupado && <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>{acompOcupado}</span>}
                      {acompAlterado && !acompOcupado && <span style={{ fontSize: 12.5, color: 'var(--coral-deep)' }}>Alterações ainda não salvas</span>}
                    </div>
                    {ult && (
                      <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 10 }}>
                        Último envio: {new Date(ult.em).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })} ({ult.por}) — {ult.enviados.length} e-mail(s)
                        {ult.erros.length > 0 && <span style={{ color: 'var(--coral-deep)' }}> · ⚠ {ult.erros.length} com erro: {ult.erros.map(x => x.erro).join('; ')}</span>}
                      </div>
                    )}
                  </div>
                )}

                {acomp && (
                  <div className="inep-card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 12, flexWrap: 'wrap' }}>
                      <h3 style={{ margin: 0 }}>Colaboradores ({lista.length})</h3>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                        <input type="checkbox" checked={acompSoPendentes} onChange={e => setAcompSoPendentes(e.target.checked)} /> Só quem tem curso pendente
                      </label>
                    </div>
                    {lista.length === 0 ? (
                      <p className="inep-sub">{acompSoPendentes ? 'Ninguém com curso pendente.' : 'Ninguém acompanhado ainda. Preencha o Início no trabalho no cadastro do usuário e coloque a pessoa numa turma com cursos.'}</p>
                    ) : (
                      <table className="inep-table">
                        <thead><tr><th>Colaborador</th><th>Início</th><th>Cursos obrigatórios</th><th>Situação</th></tr></thead>
                        <tbody>
                          {lista.map(p => {
                            const [txt, cor] = situacaoGeral(p);
                            return (
                              <tr key={p.email}>
                                <td><b>{p.nome}</b><div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{p.email}</div></td>
                                <td style={{ whiteSpace: 'nowrap' }}>{p.inicio.split('-').reverse().join('/')}</td>
                                <td>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                                    {p.cursos.map(c => <span key={c.cursoId} className="inep-tag" style={{ background: corTag[c.status][0], color: corTag[c.status][1] }}>{textoCurso(c)}</span>)}
                                  </div>
                                </td>
                                <td style={{ color: cor, fontWeight: 700 }}>{txt}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

                {acompPrevia && (
                  <Modal title="Prévia dos e-mails (como sairiam agora)" onClose={() => setAcompPrevia(null)} wide>
                    {acompPrevia.length === 0 ? (
                      <p>Nenhum e-mail sairia agora: não há pendência nem conclusão nova para avisar, ou faltam os e-mails do RH e os gerentes nos cursos.</p>
                    ) : acompPrevia.map((e, i) => (
                      <div key={i} style={{ marginBottom: 18 }}>
                        <div style={{ fontSize: 13 }}><b>Para:</b> {e.para.join(', ')}</div>
                        <div style={{ fontSize: 13, marginBottom: 6 }}><b>Assunto:</b> {e.assunto}</div>
                        <iframe title={`previa-${i}`} sandbox="" srcDoc={e.html} style={{ width: '100%', height: 420, border: '1px solid var(--line)', borderRadius: 10, background: '#fff' }} />
                      </div>
                    ))}
                  </Modal>
                )}
              </div>
            );
          })()}

          {/* ===== ADMIN: TURMAS ===== */}
          {page === 'turmas' && isAdmin && (() => {
            const membrosSel = turmaSel ? (membrosPorTurma[turmaSel.id] || []) : [];
            const emailsSel = new Set(membrosSel.map(m => m.email));
            const norm = s => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
            const tiposDisp = [...new Set(usuariosComAcesso.flatMap(u => u.tipos || []))].sort();
            const coopsDisp = [...new Set(usuariosComAcesso.flatMap(u => u.cooperativas || []))].sort();
            const candidatos = usuariosComAcesso.filter(u => {
              if (!u.email || emailsSel.has(u.email.toLowerCase())) return false;
              if (filtroTipoAdd === '__func' ? (u.tipos || []).length > 0 : filtroTipoAdd && !(u.tipos || []).includes(filtroTipoAdd)) return false;
              if (filtroCoopAdd && !(u.cooperativas || []).includes(filtroCoopAdd)) return false;
              return !buscaAdd.trim() || norm(`${u.name} ${u.email}`).includes(norm(buscaAdd));
            });
            const membrosFiltrados = membrosSel.filter(m => !buscaMembro.trim() || norm(`${m.nome} ${m.email}`).includes(norm(buscaMembro)))
              .sort((a, b) => (a.nome || '').localeCompare(b.nome || ''));
            const selCount = [...selAdd].filter(e => candidatos.some(c => c.email === e)).length;
            return (
              <div className="inep-page inep-active">
                <div className="inep-section-title" style={{ marginTop: 0 }}>
                  <h3>Turmas</h3>
                  <button className="inep-p1" style={{ background: 'var(--blue)', color: '#fff', width: 'auto', padding: '10px 18px' }} onClick={() => setTurmaForm({ nome: '', descricao: '' })}>+ Nova turma</button>
                </div>
                <p className="inep-sub">Cada turma reúne alunos. Os cursos marcados para uma turma só aparecem para quem está nela (cursos marcados "para todos" aparecem para todo mundo). Um curso pode estar em várias turmas.</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 300px) 1fr', gap: 18, alignItems: 'start' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {turmas.length === 0 && <div className="inep-card" style={{ color: 'var(--muted)' }}>Nenhuma turma ainda.</div>}
                    {turmas.map(t => (
                      <button key={t.id} className="inep-card" onClick={() => { setTurmaSelId(t.id); setSelAdd(new Set()); setBuscaMembro(''); }}
                        style={{ textAlign: 'left', padding: 14, border: turmaSelId === t.id ? '2px solid var(--blue)' : '2px solid transparent' }}>
                        <b style={{ display: 'block' }}>{t.nome}</b>
                        <small style={{ color: 'var(--muted)' }}>{(membrosPorTurma[t.id] || []).length} aluno(s) · {cursosDaTurma(t.id).length} curso(s)</small>
                      </button>
                    ))}
                  </div>

                  {!turmaSel ? (
                    <div className="inep-card" style={{ color: 'var(--muted)', textAlign: 'center', padding: 40 }}>Escolha uma turma à esquerda (ou crie uma nova).</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      <div className="inep-card">
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                          <div><h3 style={{ margin: 0 }}>{turmaSel.nome}</h3>{turmaSel.descricao && <p className="inep-sub" style={{ margin: '4px 0 0' }}>{turmaSel.descricao}</p>}</div>
                          <span style={{ display: 'flex', gap: 10, whiteSpace: 'nowrap' }}>
                            <span className="inep-lnk" onClick={() => setTurmaForm({ id: turmaSel.id, nome: turmaSel.nome, descricao: turmaSel.descricao || '' })}>Editar</span>
                            <span className="inep-lnk" style={{ color: 'var(--coral-deep)' }} onClick={() => excluirTurma(turmaSel)}>Excluir</span>
                          </span>
                        </div>
                      </div>

                      <div className="inep-card">
                        <h3 style={{ marginTop: 0 }}>Cursos desta turma</h3>
                        {todosCursos.length === 0 && <div style={{ color: 'var(--muted)' }}>Nenhum curso cadastrado.</div>}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {todosCursos.map(c => {
                            const paraTodos = c.visibilidade !== 'turmas';
                            const marcado = !paraTodos && (c.turmaIds || []).includes(turmaSel.id);
                            return (
                              <label key={c.id} className={`inep-perm-opt ${marcado ? 'inep-checked' : ''}`} style={{ minWidth: 0, opacity: paraTodos ? 0.6 : 1 }}
                                title={paraTodos ? 'Este curso está visível para todos os alunos — para restringir, abra o curso e escolha "Só as turmas marcadas".' : ''}>
                                <input type="checkbox" disabled={paraTodos} checked={marcado || paraTodos} onChange={() => alternarCursoNaTurma(c, turmaSel.id)} />
                                {c.titulo}{c.ativo === false ? ' (inativo)' : ''}{paraTodos ? ' · todos' : ''}
                              </label>
                            );
                          })}
                        </div>
                      </div>

                      <div className="inep-card">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                          <h3 style={{ margin: 0 }}>Alunos ({membrosSel.length})</h3>
                          <input value={buscaMembro} onChange={e => setBuscaMembro(e.target.value)} placeholder="🔍 Buscar na turma" style={{ padding: '7px 10px', border: '1.5px solid var(--line)', borderRadius: 8, minWidth: 220, background: 'var(--card)', color: 'var(--ink)' }} />
                        </div>
                        <div style={{ maxHeight: 300, overflowY: 'auto', marginTop: 10 }}>
                          {membrosFiltrados.length === 0 && <div style={{ color: 'var(--muted)', padding: 10 }}>{membrosSel.length ? 'Ninguém encontrado.' : 'Turma vazia — adicione alunos abaixo.'}</div>}
                          {membrosFiltrados.slice(0, 500).map(m => (
                            <div key={m.id} className="inep-rank-row" style={{ gap: 10 }}>
                              <span className="inep-mini-avatar">{(m.nome || '?').slice(0, 2).toUpperCase()}</span>
                              <span style={{ flex: 1, minWidth: 0 }}>{m.nome} <small style={{ color: 'var(--muted)' }}>{m.email}</small></span>
                              <span className="inep-lnk" style={{ color: 'var(--coral-deep)' }} onClick={() => removerMembro(m)}>Tirar</span>
                            </div>
                          ))}
                          {membrosFiltrados.length > 500 && <div style={{ color: 'var(--muted)', fontSize: 12, padding: 8 }}>Mostrando 500 de {membrosFiltrados.length} — use a busca.</div>}
                        </div>
                      </div>

                      <div className="inep-card">
                        <h3 style={{ marginTop: 0 }}>Adicionar alunos</h3>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                          <input value={buscaAdd} onChange={e => setBuscaAdd(e.target.value)} placeholder="🔍 Nome ou e-mail" style={{ flex: '1 1 200px', padding: '8px 10px', border: '1.5px solid var(--line)', borderRadius: 8, background: 'var(--card)', color: 'var(--ink)' }} />
                          <select value={filtroTipoAdd} onChange={e => setFiltroTipoAdd(e.target.value)} style={{ padding: '8px 10px', border: '1.5px solid var(--line)', borderRadius: 8, background: 'var(--card)', color: 'var(--ink)' }}>
                            <option value="">Todos os tipos</option>
                            <option value="__func">Funcionários (sem tipo)</option>
                            {tiposDisp.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                          <select value={filtroCoopAdd} onChange={e => setFiltroCoopAdd(e.target.value)} style={{ padding: '8px 10px', border: '1.5px solid var(--line)', borderRadius: 8, background: 'var(--card)', color: 'var(--ink)', maxWidth: 220 }}>
                            <option value="">Todas as cooperativas</option>
                            {coopsDisp.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
                          <small style={{ color: 'var(--muted)', flex: 1 }}>{candidatos.length} pessoa(s) com acesso ao INEP fora desta turma</small>
                          <button className="inep-add-aula" disabled={!selCount || !!salvandoTurma} onClick={() => adicionarMembros(candidatos.filter(c => selAdd.has(c.email)))}>Adicionar selecionados ({selCount})</button>
                          <button className="inep-add-aula" style={{ background: '#dcfce7', color: '#15803d' }} disabled={!candidatos.length || !!salvandoTurma}
                            onClick={() => { if (window.confirm(`Adicionar todas as ${candidatos.length} pessoas encontradas à turma "${turmaSel.nome}"?`)) adicionarMembros(candidatos); }}>
                            Adicionar todos os {candidatos.length}
                          </button>
                        </div>
                        {salvandoTurma && <div className="inep-notice" style={{ marginBottom: 8 }}>{salvandoTurma}</div>}
                        <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                          {candidatos.slice(0, 200).map(u => (
                            <label key={u.id} className="inep-rank-row" style={{ gap: 10, cursor: 'pointer' }}>
                              <input type="checkbox" checked={selAdd.has(u.email)} onChange={() => setSelAdd(s => { const n = new Set(s); if (n.has(u.email)) n.delete(u.email); else n.add(u.email); return n; })} />
                              <span style={{ flex: 1, minWidth: 0 }}>{u.name || u.email} <small style={{ color: 'var(--muted)' }}>{u.email}</small></span>
                              <small style={{ color: 'var(--muted)', textAlign: 'right' }}>{(u.tipos || []).join(', ') || 'Funcionário'}</small>
                            </label>
                          ))}
                          {candidatos.length > 200 && <div style={{ color: 'var(--muted)', fontSize: 12, padding: 8 }}>Mostrando 200 de {candidatos.length} — refine a busca ou use "Adicionar todos".</div>}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* ===== ADMIN: AVALIAÇÕES & NOTAS ===== */}
          {page === 'avaliacoes_notas' && isAdmin && (
            <div className="inep-page inep-active">
              <div className="inep-section-title" style={{ marginTop: 0 }}><h3>Notas</h3></div>
              <p className="inep-sub">Histórico acadêmico de todos os alunos.</p>
              <div style={{ overflowX: 'auto' }}>
                <table className="inep-table">
                  <thead><tr><th>Aluno</th><th>Curso</th><th>Nota</th><th>Frequência</th><th>Status</th></tr></thead>
                  <tbody>
                    {todasMatriculas.map(m => {
                      const curso = cursos.find(c => c.id === m.cursoId);
                      const freq = curso ? progressoCurso(curso, m) : 0;
                      return (
                        <tr key={m.id}>
                          <td><span className="inep-mini-avatar">{(m.userNome || '?').slice(0, 2).toUpperCase()}</span>{m.userNome}</td>
                          <td>{curso?.titulo || m.cursoId}</td>
                          <td>{typeof m.notaAvaliacao === 'number' ? m.notaAvaliacao : '—'}</td>
                          <td>{freq}%</td>
                          <td><span className="inep-tag" style={{ background: m.concluidoEm ? '#dcfce7' : '#fef9c3', color: m.concluidoEm ? '#15803d' : '#a16207' }}>{m.concluidoEm ? 'Concluído' : 'Em andamento'}</span></td>
                        </tr>
                      );
                    })}
                    {todasMatriculas.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--muted)' }}>Nenhum registro ainda.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ===== ADMIN: CERTIFICADOS (modelo oficial) ===== */}
          {page === 'certificados_admin' && isAdmin && (
            <div className="inep-page inep-active">
              <div className="inep-section-title" style={{ marginTop: 0 }}>
                <h3>Modelo de certificado</h3>
                <button className="inep-p1" style={{ background: 'var(--blue)', color: '#fff', width: 'auto', padding: '10px 18px' }} onClick={verExemploPdf}>📄 Ver exemplo em PDF</button>
              </div>
              <p className="inep-sub">Este é o certificado oficial que o aluno baixa ao concluir um curso (frente e verso), aqui preenchido com dados de exemplo.</p>
              <ModeloOficialExemplo />
              <div className="inep-card" style={{ marginTop: 16 }}>
                <h3 style={{ marginTop: 0 }}>De onde vem cada informação</h3>
                <div className="inep-fields">
                  {[
                    ['Nome completo e CPF', 'Perfil do aluno — pedidos antes do 1º download'],
                    ['Curso, responsável, média mínima e módulos', 'Cadastro do curso (Cursos & módulos)'],
                    ['Carga horária', 'Campo do curso ou soma da duração das aulas'],
                    ['Início, conclusão, frequência e média final', 'Matrícula e avaliação do aluno'],
                    ['Instituição e cidade', 'Personalização → Textos → Avaliações e certificados'],
                    ['CNPJ', 'Personalização → Rodapé'],
                    ['Código e QR code', 'Gerados sozinhos; o QR abre gestaoquality.com.br/validar/…'],
                  ].map(([k, v]) => <div key={k} className="inep-field"><b>{k}</b><span>{v}</span></div>)}
                </div>
                <div className="inep-notice" style={{ marginTop: 12 }}>Assinaturas, nomes e cargos dos signatários, logo, cores e o texto fixo do certificado fazem parte do modelo oficial. Para mudar algum deles é preciso uma nova versão do modelo.</div>
              </div>
            </div>
          )}

          {/* ===== ADMIN: MENSAGENS ===== */}
          {page === 'mensagens_admin' && isAdmin && (
            <div className="inep-page inep-active">
              <div className="inep-section-title" style={{ marginTop: 0 }}><h3>Mensagens — visão do instrutor</h3></div>
              <ChatPanel />
            </div>
          )}

          {/* ===== ADMIN: RELATÓRIOS ===== */}
          {page === 'relatorios' && isAdmin && (
            <div className="inep-page inep-active">
              <div className="inep-section-title" style={{ marginTop: 0 }}><h3>Relatórios</h3></div>
              {(() => {
                const hojeBR = new Date(Date.now() - 3 * 3600 * 1000);
                const dia = hojeBR.toISOString().slice(0, 10);
                const dow = (hojeBR.getUTCDay() + 6) % 7; // segunda = 0
                const inicioSemana = new Date(hojeBR.getTime() - dow * 86400000).toISOString().slice(0, 10);
                const maior = lista => lista.reduce((m, p) => ((p.pico || 0) > (m?.pico || 0) ? p : m), null);
                const periodos = [
                  ['Hoje', maior(picos.filter(p => p.dia === dia))],
                  ['Esta semana', maior(picos.filter(p => p.dia >= inicioSemana))],
                  ['Este mês', maior(picos.filter(p => p.dia?.slice(0, 7) === dia.slice(0, 7)))],
                  ['Este ano', maior(picos.filter(p => p.dia?.slice(0, 4) === dia.slice(0, 4)))],
                ];
                const ultimos30 = [...picos].filter(p => p.dia).sort((a, b) => a.dia.localeCompare(b.dia)).slice(-30);
                const escala = Math.max(1, ...ultimos30.map(p => Math.max(p.pico || 0, p.unicos || 0)));
                const fmtDia = d => d ? `${d.slice(8, 10)}/${d.slice(5, 7)}` : '—';
                const fmtHora = iso => iso ? new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '';
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 18, marginBottom: 18 }}>
                    <div className="inep-card">
                      <h3 style={{ marginTop: 0 }}>🟢 Online agora <span style={{ color: 'var(--mint)' }}>{onlineAgora.length}</span></h3>
                      <p className="inep-sub">Atualiza a cada 30 segundos. Conta quem está com o INEP aberto e visível.</p>
                      <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                        {onlineAgora.length === 0 && <div style={{ color: 'var(--muted)', padding: 8 }}>Ninguém online neste momento.</div>}
                        {onlineAgora.map(o => (
                          <div key={o.email} className="inep-rank-row" style={{ gap: 10 }}>
                            <span className="inep-mini-avatar">{(o.nome || '?').slice(0, 2).toUpperCase()}</span>
                            <span style={{ flex: 1, minWidth: 0 }}>{o.nome} <small style={{ color: 'var(--muted)' }}>{o.tipo === 'admin' ? 'admin' : 'aluno'}</small></span>
                            <small style={{ color: 'var(--muted)' }}>desde {fmtHora(o.inicio)}</small>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="inep-card">
                      <h3 style={{ marginTop: 0 }}>Pico de acessos simultâneos</h3>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 }}>
                        {periodos.map(([rotulo, p]) => (
                          <div key={rotulo} style={{ background: 'var(--bg)', borderRadius: 12, padding: '10px 12px' }}>
                            <small style={{ color: 'var(--muted)', fontWeight: 700 }}>{rotulo}</small>
                            <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--blue)' }}>{p?.pico || 0}</div>
                            <small style={{ color: 'var(--muted)' }}>{p?.pico ? `${fmtDia(p.dia)} às ${fmtHora(p.picoEm)}` : '—'}</small>
                          </div>
                        ))}
                      </div>
                      <small style={{ color: 'var(--muted)' }}>Últimos 30 dias — <span style={{ color: 'var(--blue)' }}>■</span> pico simultâneo · <span style={{ color: 'var(--cyan)' }}>■</span> pessoas diferentes no dia</small>
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 130, marginTop: 8, borderBottom: '1px solid var(--line)' }}>
                        {ultimos30.length === 0 && <div style={{ color: 'var(--muted)', fontSize: 12.5, alignSelf: 'center', margin: '0 auto' }}>Os dados começam a aparecer conforme os alunos acessam.</div>}
                        {ultimos30.map(p => (
                          <div key={p.dia} title={`${fmtDia(p.dia)}: pico ${p.pico || 0} · ${p.unicos || 0} pessoa(s) no dia`} style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 1, height: '100%' }}>
                            <div style={{ flex: 1, height: `${(p.pico || 0) / escala * 100}%`, background: 'var(--blue)', borderRadius: '3px 3px 0 0', minHeight: p.pico ? 2 : 0 }} />
                            <div style={{ flex: 1, height: `${(p.unicos || 0) / escala * 100}%`, background: 'var(--cyan)', borderRadius: '3px 3px 0 0', minHeight: p.unicos ? 2 : 0, opacity: 0.7 }} />
                          </div>
                        ))}
                      </div>
                      {ultimos30.length > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--muted)', marginTop: 4 }}><span>{fmtDia(ultimos30[0].dia)}</span><span>{fmtDia(ultimos30[ultimos30.length - 1].dia)}</span></div>}
                      <button className="inep-add-aula" style={{ marginTop: 12 }} onClick={() => downloadCsv('inep_acessos_por_dia.csv', [['Dia', 'Pico simultâneo', 'Horário do pico', 'Pessoas diferentes no dia'], ...[...picos].sort((a, b) => (a.dia || '').localeCompare(b.dia || '')).map(p => [p.dia, p.pico || 0, fmtHora(p.picoEm), p.unicos || 0])])}>📥 Acessos por dia (CSV)</button>
                    </div>
                  </div>
                );
              })()}
              <p className="inep-sub">Exporte dados da plataforma.</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 18 }}>
                <div className="inep-card">
                  <h3>Conclusão por curso</h3><p className="inep-sub">Percentual médio de conclusão dos alunos matriculados</p>
                  <div className="inep-report-stats">
                    {conclusaoPorCurso.length === 0 && <div style={{ color: 'var(--muted)' }}>Sem cursos ainda.</div>}
                    {conclusaoPorCurso.map(c => (<div key={c.titulo}><b>{c.pct}%</b><span>{c.titulo}</span></div>))}
                  </div>
                </div>
                <div className="inep-card">
                  <h3>Exportar dados</h3><p className="inep-sub">Gere relatórios para análise externa</p>
                  <button className="inep-add-aula" style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 8 }} onClick={exportRelatorioGeral}>📋 Relatório geral (CSV)</button>
                  <button className="inep-add-aula" style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 8, background: '#fdeceb', color: 'var(--coral-deep)' }} onClick={exportHistoricoNotas}>📈 Histórico de notas (CSV)</button>
                  <button className="inep-add-aula" style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 8, background: '#dcfce7', color: '#15803d' }} onClick={exportCertificadosCsv}>🏅 Certificados emitidos (CSV)</button>
                  <button className="inep-add-aula" style={{ display: 'block', width: '100%', textAlign: 'left', background: '#fff7eb', color: '#80551e' }} onClick={exportFrequenciaCsv}>📊 Frequência por aluno (CSV)</button>
                </div>
              </div>
            </div>
          )}

          {/* ===== ADMIN: PERSONALIZAÇÃO ===== */}
          {page === 'personalizacao' && isAdmin && draftConfig && (
            <div className="inep-page inep-active">
              <div className="inep-section-title" style={{ marginTop: 0 }}><h3>Personalização</h3></div>
              <p className="inep-sub">Textos, cores, logo, "Quem somos" e rodapé de todas as telas do INEP. Nada muda para os alunos até você clicar em Salvar.</p>
              <InepPersonalizacaoAdmin draft={draftConfig} setDraft={setDraftConfig} salvar={salvarPersonalizacao}
                heroPadrao={{ titulo: heroTitulo, texto: heroTexto }} cursosCores={CORES_CURSO} />
            </div>
          )}

          {/* ===== ADMIN: CONTROLE DE ACESSO ===== */}
          {page === 'acesso' && isAdmin && (
            <div className="inep-page inep-active">
              <div className="inep-section-title" style={{ marginTop: 0 }}><h3>Controle de acesso</h3>{isAdminMaster && <button className="inep-p1" style={{ background: 'var(--blue)', color: '#fff', width: 'auto', padding: '10px 18px' }} onClick={abrirGrant}>+ Liberar acesso</button>}</div>
              <p className="inep-sub">Por padrão ninguém acessa o INEP — libere aqui quem pode entrar como aluno ou como admin, e o que cada admin pode ver.</p>
              <div className="inep-card">
                <table className="inep-table">
                  <thead><tr><th>Pessoa</th><th>E-mail</th><th>Acesso</th><th></th></tr></thead>
                  <tbody>
                    <tr>
                      <td><span className="inep-mini-avatar">{userNome.slice(0, 2).toUpperCase()}</span>Você{isAdminMaster ? ' (admin master)' : ''}</td>
                      <td>{email}</td>
                      <td><span className="inep-perm-chip inep-master-chip">Acesso total</span></td>
                      <td>—</td>
                    </tr>
                    {usuariosComAcesso.filter(u => u.email !== email).map(u => {
                      const restrito = inepAdmins.find(a => (a.email || '').toLowerCase() === (u.email || '').toLowerCase());
                      return (
                        <tr key={u.id}>
                          <td>{u.name || '—'}</td>
                          <td>{u.email}</td>
                          <td>
                            {restrito
                              ? (restrito.abas || []).map(id => <span key={id} className="inep-perm-chip">{ADMIN_TABS.find(t => t.id === id)?.label || id}</span>)
                              : <span className="inep-perm-chip">🎓 Aluno</span>}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {isAdminMaster && (<>
                              <span className="inep-lnk" onClick={() => editarGrant(u)}>Editar</span>{' '}
                              <span className="inep-lnk" style={{ color: 'var(--coral-deep)' }} onClick={() => revogarAcesso(u)}>Revogar</span>
                            </>)}
                          </td>
                        </tr>
                      );
                    })}
                    {usuariosComAcesso.filter(u => u.email !== email).length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--muted)' }}>Ninguém mais tem acesso liberado ainda.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <InepRodape config={config} />
        </main>
      </div>

      {/* ── Editor de curso (admin) ── */}
      {editingCurso && (
        <Modal title={editingCurso.id ? 'Editar curso' : 'Novo curso'} onClose={() => setEditingCurso(null)} wide>
          <div className="inep-form-grid">
            <Field label="Título do curso"><input value={editingCurso.titulo} onChange={e => setEditingCurso(c => ({ ...c, titulo: e.target.value }))} /></Field>
            <Field label="Categoria"><input value={editingCurso.categoria || ''} onChange={e => setEditingCurso(c => ({ ...c, categoria: e.target.value }))} placeholder="Ex.: Segurança do paciente" /></Field>
            <Field label="Cor da capa">
              <select value={editingCurso.cor} onChange={e => setEditingCurso(c => ({ ...c, cor: e.target.value }))}>
                <option value="blue">Azul</option><option value="coral">Coral</option><option value="mint">Verde</option>
              </select>
            </Field>
            <Field label="Responsável pelo curso (sai no certificado)"><input value={editingCurso.responsavel || ''} onChange={e => setEditingCurso(c => ({ ...c, responsavel: e.target.value }))} placeholder="Ex.: Dr. André Ricardo A. da Silva" /></Field>
            <Field label="Média mínima para aprovação (0 a 10)"><input type="number" min="0" max="10" step="0.5" value={editingCurso.notaMinima ?? ''} onChange={e => setEditingCurso(c => ({ ...c, notaMinima: e.target.value }))} placeholder="7" /></Field>
            <Field label="Carga horária (horas)"><input type="number" min="0" step="0.5" value={editingCurso.cargaHoraria || ''} onChange={e => setEditingCurso(c => ({ ...c, cargaHoraria: e.target.value }))} placeholder="Ex.: 2 (vazio = soma das aulas)" /></Field>
            <div className="inep-full">
              <Field label="Gerente da área (recebe por e-mail a situação dos colaboradores novos neste curso)">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                  {(editingCurso.gerentes || []).map(g => (
                    <span key={g.email} className="inep-tag" style={{ background: 'var(--bg)', border: '1px solid var(--line)', color: 'var(--ink)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      👔 {g.nome || g.email}
                      <span className="inep-lnk" style={{ color: 'var(--coral-deep)' }} title="Tirar" onClick={() => setEditingCurso(c => ({ ...c, gerentes: (c.gerentes || []).filter(x => x.email !== g.email) }))}>✕</span>
                    </span>
                  ))}
                  <select value="" style={{ flex: '1 1 240px' }} onChange={e => {
                    const u = usuariosQuality.find(x => x.email === e.target.value);
                    if (u) setEditingCurso(c => ({ ...c, gerentes: [...(c.gerentes || []), { email: u.email.toLowerCase(), nome: u.name || u.email }] }));
                  }}>
                    <option value="">{(editingCurso.gerentes || []).length ? '+ Adicionar outro gerente…' : 'Escolha o gerente da área…'}</option>
                    {usuariosQuality.filter(u => !(editingCurso.gerentes || []).some(g => g.email === u.email.toLowerCase()))
                      .map(u => <option key={u.id} value={u.email}>{u.name || u.email} — {u.email}</option>)}
                  </select>
                </div>
              </Field>
            </div>
            <div className="inep-full">
              <Field label="Descrição"><textarea value={editingCurso.descricao || ''} onChange={e => setEditingCurso(c => ({ ...c, descricao: e.target.value }))} rows={2} /></Field>
            </div>
            <div className="inep-full">
              <Field label="Imagem de capa do curso (opcional — sem imagem, usa a cor)">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 160, height: 90, borderRadius: 10, border: '1px dashed var(--line)', overflow: 'hidden', background: editingCurso.capaUrl ? `center/cover url(${editingCurso.capaUrl})` : 'var(--bg)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {!editingCurso.capaUrl && <span style={{ fontSize: 11, color: 'var(--muted)' }}>sem imagem</span>}
                  </div>
                  <label className="inep-p1" style={{ display: 'inline-block', cursor: 'pointer', fontSize: 12.5, padding: '6px 12px', opacity: enviandoCapaCurso ? 0.6 : 1 }}>
                    {enviandoCapaCurso ? 'Enviando…' : (editingCurso.capaUrl ? 'Trocar imagem' : 'Enviar imagem')}
                    <input type="file" accept="image/*" hidden disabled={enviandoCapaCurso}
                      onChange={async e => {
                        const f = e.target.files?.[0]; e.target.value = ''; if (!f) return;
                        setEnviandoCapaCurso(true);
                        try { const { url } = await uploadArquivo(f, 'inep/capas'); setEditingCurso(c => ({ ...c, capaUrl: url })); }
                        catch (err) { alert('Erro ao enviar imagem: ' + err.message); }
                        finally { setEnviandoCapaCurso(false); }
                      }} />
                  </label>
                  {editingCurso.capaUrl && <span className="inep-lnk" style={{ color: 'var(--coral-deep)', fontSize: 12 }} onClick={() => setEditingCurso(c => ({ ...c, capaUrl: '' }))}>Remover</span>}
                </div>
                <small style={{ color: 'var(--muted)' }}>Tamanho ideal: 1280 × 720 (formato de vídeo). A imagem aparece no card do catálogo.</small>
              </Field>
            </div>
          </div>

          <h3 style={{ marginTop: 16 }}>Quem vê este curso</h3>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            {[['todos', '👥 Todos os alunos'], ['turmas', '🧑‍🤝‍🧑 Só as turmas marcadas']].map(([v, l]) => {
              const atual = editingCurso.visibilidade === 'turmas' ? 'turmas' : 'todos';
              return (
                <button key={v} onClick={() => setEditingCurso(c => ({ ...c, visibilidade: v }))}
                  style={{ padding: '8px 14px', borderRadius: 9, fontWeight: 700, fontSize: 12.8, background: atual === v ? 'var(--blue)' : 'var(--bg)', color: atual === v ? '#fff' : 'var(--ink)' }}>{l}</button>
              );
            })}
          </div>
          {editingCurso.visibilidade === 'turmas' && (
            <div className="inep-card" style={{ marginBottom: 10 }}>
              {turmas.length === 0 && <div style={{ color: 'var(--muted)', fontSize: 13 }}>Nenhuma turma criada ainda — crie em <b>Turmas</b> no menu do admin.</div>}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {turmas.map(t => {
                  const marcado = (editingCurso.turmaIds || []).includes(t.id);
                  return (
                    <label key={t.id} className={`inep-perm-opt ${marcado ? 'inep-checked' : ''}`} style={{ minWidth: 0 }}>
                      <input type="checkbox" checked={marcado} onChange={() => setEditingCurso(c => ({ ...c, turmaIds: marcado ? (c.turmaIds || []).filter(x => x !== t.id) : [...(c.turmaIds || []), t.id] }))} />
                      {t.nome} <small style={{ color: 'var(--muted)' }}>({(membrosPorTurma[t.id] || []).length})</small>
                    </label>
                  );
                })}
              </div>
              {turmas.length > 0 && !(editingCurso.turmaIds || []).length && <div style={{ color: 'var(--coral-deep)', fontSize: 12.5, marginTop: 8 }}>Nenhuma turma marcada: nenhum aluno vai ver este curso.</div>}
            </div>
          )}

          <h3 style={{ marginTop: 20 }}>Módulos e aulas</h3>
          {(editingCurso.modulos || []).map((mod, mi) => (
            <div key={mod.id} className="inep-card" style={{ marginBottom: 10 }}>
              <input value={mod.titulo} onChange={e => setEditingCurso(c => { const modulos = [...c.modulos]; modulos[mi] = { ...mod, titulo: e.target.value }; return { ...c, modulos }; })}
                placeholder="Título do módulo" style={{ width: '100%', padding: '8px 11px', border: '1.5px solid var(--line)', borderRadius: 8, marginBottom: 8, fontWeight: 700 }} />
              {(mod.aulas || []).map((a, ai) => (
                <div key={a.id} className="inep-aula-row">
                  <input value={a.titulo} placeholder="Título da aula" onChange={e => setEditingCurso(c => { const modulos = [...c.modulos]; const aulas = [...modulos[mi].aulas]; aulas[ai] = { ...a, titulo: e.target.value }; modulos[mi] = { ...modulos[mi], aulas }; return { ...c, modulos }; })} />
                  <AulaVideoCampo aula={a} progresso={progressoVideo[a.id]}
                    onMudar={campos => setEditingCurso(c => { const modulos = [...c.modulos]; const aulas = [...modulos[mi].aulas]; aulas[ai] = { ...aulas[ai], ...campos }; modulos[mi] = { ...modulos[mi], aulas }; return { ...c, modulos }; })}
                    onArquivo={file => enviarVideoDaAula(mi, ai, a.id, file)} />
                  <input value={a.duracaoMin} placeholder="min" onChange={e => setEditingCurso(c => { const modulos = [...c.modulos]; const aulas = [...modulos[mi].aulas]; aulas[ai] = { ...a, duracaoMin: e.target.value }; modulos[mi] = { ...modulos[mi], aulas }; return { ...c, modulos }; })} />
                  <button className="inep-rm-btn" onClick={() => setEditingCurso(c => { const modulos = [...c.modulos]; modulos[mi] = { ...modulos[mi], aulas: modulos[mi].aulas.filter((_, i) => i !== ai) }; return { ...c, modulos }; })}>✕</button>
                </div>
              ))}
              <button className="inep-add-aula" onClick={() => setEditingCurso(c => { const modulos = [...c.modulos]; modulos[mi] = { ...modulos[mi], aulas: [...(modulos[mi].aulas || []), novaAula()] }; return { ...c, modulos }; })}>+ Adicionar aula</button>
            </div>
          ))}
          <button className="inep-add-aula" style={{ background: '#f1f5f9', color: '#334155' }} onClick={() => setEditingCurso(c => ({ ...c, modulos: [...(c.modulos || []), novoModulo()] }))}>+ Adicionar módulo</button>

          <h3 ref={avaliacaoSecRef} style={{ marginTop: 20 }}>Avaliação final (obrigatória)</h3>
          {!(editingCurso.avaliacao?.perguntas?.length > 0) && (
            <div className="inep-notice" style={{ marginBottom: 10 }}>Sem pelo menos 1 pergunta o curso fica salvo, mas <b>não aparece para os alunos</b> — a média da avaliação vai no certificado oficial.</div>
          )}
          {(editingCurso.avaliacao?.perguntas || []).map((p, pi) => (
            <div key={pi} className="inep-card" style={{ marginBottom: 10 }}>
              <input value={p.pergunta} placeholder="Pergunta" onChange={e => setEditingCurso(c => { const perguntas = [...c.avaliacao.perguntas]; perguntas[pi] = { ...p, pergunta: e.target.value }; return { ...c, avaliacao: { perguntas } }; })}
                style={{ width: '100%', padding: '8px 11px', border: '1.5px solid var(--line)', borderRadius: 8, marginBottom: 8, fontWeight: 700 }} />
              {[0, 1, 2, 3].map(oi => (
                <div key={oi} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                  <input type="radio" checked={p.correta === oi} onChange={() => setEditingCurso(c => { const perguntas = [...c.avaliacao.perguntas]; perguntas[pi] = { ...p, correta: oi }; return { ...c, avaliacao: { perguntas } }; })} />
                  <input value={p.opcoes?.[oi] || ''} placeholder={`Opção ${oi + 1}`} onChange={e => setEditingCurso(c => { const perguntas = [...c.avaliacao.perguntas]; const opcoes = [...(p.opcoes || ['', '', '', ''])]; opcoes[oi] = e.target.value; perguntas[pi] = { ...p, opcoes }; return { ...c, avaliacao: { perguntas } }; })} style={{ flex: 1, padding: '7px 10px', border: '1.5px solid var(--line)', borderRadius: 8 }} />
                </div>
              ))}
              <button className="inep-rm-btn" onClick={() => setEditingCurso(c => ({ ...c, avaliacao: { perguntas: c.avaliacao.perguntas.filter((_, i) => i !== pi) } }))}>✕ Remover pergunta</button>
            </div>
          ))}
          <button className="inep-add-aula" onClick={() => setEditingCurso(c => ({ ...c, avaliacao: { perguntas: [...(c.avaliacao?.perguntas || []), { pergunta: '', opcoes: ['', '', '', ''], correta: 0 }] } }))}>+ Adicionar pergunta</button>

          <div style={{ marginTop: 20, textAlign: 'right' }}>
            <button className="inep-p1" style={{ background: 'var(--blue)', color: '#fff', width: 'auto', padding: '11px 22px' }} onClick={salvarCurso}>💾 Salvar curso</button>
          </div>
        </Modal>
      )}

      {/* ── Dados para o certificado oficial (nome completo + CPF) ── */}
      {certPedirDados && (
        <Modal title="Dados do certificado" onClose={() => setCertPedirDados(null)}>
          <p className="inep-sub">O certificado oficial sai com seu <b>nome completo</b> e <b>CPF</b>. Confira com atenção — depois de emitido, ele não muda. Os dados também ficam salvos no seu perfil.</p>
          {certPedirDados.erro && <div style={{ background: '#fdeceb', color: 'var(--coral-deep)', padding: '10px 14px', borderRadius: 8, fontSize: 12.5, marginBottom: 12 }}>{certPedirDados.erro}</div>}
          <Field label="Nome completo"><input value={certPedirDados.nome} onChange={e => setCertPedirDados(d => ({ ...d, nome: e.target.value, erro: '' }))} placeholder="Ex.: Maria Fernanda de Souza Lima" autoFocus /></Field>
          <Field label="CPF"><input value={certPedirDados.cpf} inputMode="numeric" onChange={e => setCertPedirDados(d => ({ ...d, cpf: formatarCpf(e.target.value) || e.target.value.replace(/[^\d.-]/g, '').slice(0, 14), erro: '' }))} placeholder="000.000.000-00" /></Field>
          <p style={{ fontSize: 12.5, color: 'var(--muted)', margin: '4px 0 14px' }}>Vai sair assim: <b>{nomeProprio(certPedirDados.nome) || '—'}</b></p>
          <button className="inep-p1" style={{ background: 'var(--blue)', color: '#fff', width: 'auto', padding: '10px 18px' }} onClick={confirmarDadosCertificado}>Confirmar e gerar certificado</button>
        </Modal>
      )}

      {/* ── Perfil de um aluno (admin, só leitura) ── */}
      {perfilAluno && (
        <Modal title={`Perfil — ${perfilAluno.nome}`} onClose={() => setPerfilAluno(null)} wide>
          <InepPerfil somenteLeitura email={perfilAluno.email} nome={perfilAluno.nome} userDoc={perfilAluno.userDoc} perfil={perfilAluno.perfil}
            turmasNomes={membros.filter(m => m.email === perfilAluno.email).map(m => turmas.find(t => t.id === m.turmaId)?.nome).filter(Boolean)} />
        </Modal>
      )}

      {/* ── Criar/editar turma ── */}
      {turmaForm && (
        <Modal title={turmaForm.id ? 'Editar turma' : 'Nova turma'} onClose={() => setTurmaForm(null)}>
          <Field label="Nome da turma"><input value={turmaForm.nome} onChange={e => setTurmaForm(f => ({ ...f, nome: e.target.value }))} placeholder="Ex.: Técnicos do Homecare" autoFocus /></Field>
          <Field label="Descrição (opcional)"><textarea rows={2} value={turmaForm.descricao} onChange={e => setTurmaForm(f => ({ ...f, descricao: e.target.value }))} placeholder="Ex.: Técnicos de enfermagem das cooperativas do Homecare" /></Field>
          <button className="inep-p1" style={{ background: 'var(--blue)', color: '#fff', width: 'auto', padding: '10px 18px' }} onClick={salvarTurma} disabled={!turmaForm.nome?.trim()}>Salvar turma</button>
        </Modal>
      )}

      {/* ── Atalho "Lançar avaliação": escolher em qual curso ── */}
      {escolherCursoAvaliacao && (
        <Modal title="Lançar avaliação" onClose={() => setEscolherCursoAvaliacao(false)}>
          <p className="inep-sub">Escolha o curso para adicionar ou editar as perguntas da avaliação final. Você será levado direto pra essa seção.</p>
          {cursos.length === 0 && <div className="inep-card" style={{ marginTop: 10 }}>Nenhum curso cadastrado ainda. Crie um curso primeiro.</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: cursos.length ? 10 : 0 }}>
            {cursos.map(c => (
              <button key={c.id} className="inep-add-aula" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', textAlign: 'left' }}
                onClick={() => abrirAvaliacaoDoCurso(c)}>
                <span>{c.titulo}</span>
                <span style={{ fontSize: 11, color: 'var(--muted)' }}>{c.avaliacao?.perguntas?.length || 0} pergunta(s)</span>
              </button>
            ))}
          </div>
        </Modal>
      )}

      {/* ── Conceder/editar acesso administrativo ── */}
      {grantModal && (
        <Modal title={grantModal.userId ? 'Editar acesso' : 'Liberar acesso ao INEP'} onClose={() => setGrantModal(null)}>
          {grantErro && <div style={{ background: '#fdeceb', color: 'var(--coral-deep)', padding: '10px 14px', borderRadius: 8, fontSize: 12.5, marginBottom: 14 }}>{grantErro}</div>}
          <Field label="Nome"><input value={grantModal.nome} onChange={e => setGrantModal(g => ({ ...g, nome: e.target.value }))} placeholder="Ex.: Enf. Renata Alves" /></Field>
          <Field label="E-mail (precisa já ser um usuário do Quality)"><input value={grantModal.email} onChange={e => setGrantModal(g => ({ ...g, email: e.target.value }))} placeholder="nome@prontobaby.com.br" disabled={!!grantModal.userId} /></Field>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 6 }}>Tipo de acesso</div>
          <div className="inep-flip" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <button onClick={() => setGrantModal(g => ({ ...g, tipo: 'aluno' }))} style={{ padding: '9px 16px', borderRadius: 9, fontWeight: 700, fontSize: 12.8, background: grantModal.tipo === 'aluno' ? 'var(--blue)' : 'var(--bg)', color: grantModal.tipo === 'aluno' ? '#fff' : 'var(--ink)' }}>🎓 Aluno</button>
            <button onClick={() => setGrantModal(g => ({ ...g, tipo: 'admin' }))} style={{ padding: '9px 16px', borderRadius: 9, fontWeight: 700, fontSize: 12.8, background: grantModal.tipo === 'admin' ? 'var(--blue)' : 'var(--bg)', color: grantModal.tipo === 'admin' ? '#fff' : 'var(--ink)' }}>🛠️ Admin restrito</button>
          </div>
          {grantModal.tipo === 'admin' && (<>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 6 }}>Abas que essa pessoa poderá acessar</div>
            <div className="inep-perm-grid-adm">
              {ADMIN_TABS.map(t => (
                <label key={t.id} className={`inep-perm-opt ${grantModal.abas.includes(t.id) ? 'inep-checked' : ''}`}>
                  <input type="checkbox" checked={grantModal.abas.includes(t.id)} onChange={() => toggleAba(t.id)} />
                  {t.ic} {t.label}
                </label>
              ))}
            </div>
          </>)}
          <button className="inep-p1" style={{ background: 'var(--blue)', color: '#fff', width: 'auto', padding: '10px 18px', marginTop: 10 }} onClick={salvarGrant}>Salvar acesso</button>
        </Modal>
      )}
    </div>
  );
}
